import { formatCurrency, weekdayForDate } from "@booking-seconds/utils";

export type PricebookRecord = Record<string, unknown>;

export type PricebookDiscountType = "none" | "fixed" | "percentage";
export type PricebookPricingDisplayMode = "exact" | "manual_review" | "remodeling_hourly" | "remodeling_review";

export type PricebookService = PricebookRecord & {
  id: string;
  name: string;
  active?: boolean | number;
};

export type PricebookServiceOption = PricebookRecord & {
  id: string;
  name: string;
  active?: boolean | number;
};

export type PricebookServiceScenario = PricebookRecord & {
  id: string;
  name: string;
  code: string;
  baseServiceId?: string;
  base_service_id?: string;
  active?: boolean | number;
};

export type PricebookFrequencyRule = PricebookRecord & {
  id: string;
  name: string;
  active?: boolean | number;
};

export type PricebookPromotion = PricebookRecord & {
  id?: string;
  code?: string;
  active?: boolean | number;
};

export type BuildingCommonAreasBucket =
  | "1_3"
  | "4_6"
  | "7_9"
  | "10_12"
  | "13_15"
  | "over_15";

export type BuildingCommonAreasPricingMode = "instant" | "manual";

export type BuildingCommonAreasPricingRule = {
  bucket: BuildingCommonAreasBucket;
  label: string;
  pricingMode: BuildingCommonAreasPricingMode;
  basePriceCents: number;
  baseMinutes: number;
  displayOrder: number;
};

export type ResidentialFormulaBucket = "apartment_like" | "townhouse" | "single_family";

export type ResidentialFormulaRule = {
  bucket: ResidentialFormulaBucket;
  label: string;
  baseTeamMinutes: number;
  minutesPerSquareFoot: number;
  minutesPerBedroom: number;
  minutesPerFullBathroom: number;
  minutesPerHalfBathroom: number;
  minutesPerAdditionalLevel: number;
};

export type ResidentialComponentFormulaRule = {
  bucket: ResidentialFormulaBucket;
  label: string;
  setupTeamMinutes: number;
  kitchenTeamMinutes: number;
  minutesPerSquareFoot: number;
  minutesPerBedroom: number;
  minutesPerFullBathroom: number;
  minutesPerHalfBathroom: number;
  minutesPerExtraHalfLevel: number;
};

export type ResidentialServiceType =
  | "standard_residential_cleaning"
  | "deep_cleaning"
  | "move_in_cleaning"
  | "move_out_cleaning";

export type ResidentialHourlyServiceProfile = {
  serviceType: ResidentialServiceType;
  label: string;
  hourlyRateCentsPerMaidHour: number;
  minimumTeamMinutes: number;
  serviceTimeMultiplier: number;
};

export type ResidentialConditionArea = "kitchen" | "bathrooms" | "bedrooms" | "otherAreas";

export type ResidentialConditionAdjustmentPercentages = Record<ResidentialConditionArea, Record<number, number>>;
export type ResidentialComponentConditionMultipliers = Record<ResidentialConditionArea, Record<number, number>>;

export type ResidentialSkipAreaPropertyGroup = "apartment_condo" | "house";

export type ResidentialSkipAreaDurationAreaCode =
  | "kitchen"
  | "full_bathroom"
  | "half_bathroom"
  | "bedroom"
  | "living_areas"
  | "living_room"
  | "dining_room"
  | "hallways";

export type ResidentialSkipAreaDurationRule = {
  propertyGroup: ResidentialSkipAreaPropertyGroup;
  areaCode: ResidentialSkipAreaDurationAreaCode;
  label: string;
  laborMinutes: number;
  squareFootageMin?: number;
  squareFootageMax?: number;
  displayOrder: number;
};

export type ResidentialFormulaLevelSquareFootageMinimum = {
  levels: number;
  minimumSquareFeet: number;
};

export type OfficeCommercialFormulaRule = {
  hourlyRateCentsPerMaidHour: number;
  minimumTeamMinutes: number;
  baseTeamMinutes: number;
  minutesPerSquareFoot: number;
  minutesPerWorkstation: number;
  minutesPerPrivateRoom: number;
  minutesPerRestroom: number;
  minutesPerBreakroom: number;
  minutesPerAdditionalLevel: number;
  manualReviewSquareFeetMax: number;
};

export type PricebookSettings = {
  version: number;
  advancedRules?: PricebookRecord;
};

export type PricebookCatalog = {
  accountId?: string;
  services: PricebookService[];
  serviceOptions: PricebookServiceOption[];
  serviceScenarios: PricebookServiceScenario[];
  frequencyRules: PricebookFrequencyRule[];
  promotions: PricebookPromotion[];
  settings?: PricebookSettings;
};

export type ArrivalFlexibilityQuoteSettings = {
  enabled?: boolean;
  startTime?: string;
  endTime?: string;
  discountType?: "fixed" | "percentage";
  discountValue?: number;
  excludedWindowStartTimes?: string[];
};

export type CustomerSuppliesEquipmentQuoteSettings = {
  enabled?: boolean;
  discountAmount?: number;
  requiredItems?: string[];
};

export type PricebookQuoteInput = {
  serviceId: string;
  optionIds?: string[];
  frequencyRuleId?: string;
  promoCode?: string;
  serviceDate?: string;
  adminDetails?: Record<string, string>;
  teamSize?: number;
  taxRate?: number;
  depositRate?: number;
  arrivalFlexibility?: {
    settings?: ArrivalFlexibilityQuoteSettings | null;
    localStartTime?: string;
  };
  customerSuppliesEquipment?: CustomerSuppliesEquipmentQuoteSettings | null;
  now?: number | Date;
};

export type SkipAreaCode =
  | "kitchen"
  | "bathrooms"
  | "bedrooms"
  | "living_areas"
  | "living_room"
  | "dining_room"
  | "hallways";

export type SkipAreaDeductionRow = {
  code: SkipAreaCode;
  label: string;
  quantityLabel: string;
  oneMaidMinutes: number;
  twoMaidMinutes: number;
  amountCents: number;
};

export type PricebookQuoteOptionDetail = {
  id: string;
  name: string;
  priceDeltaAmount: number;
  durationDeltaMinutes: number;
  quantity?: number;
  quantityLabel?: string;
};

export type PricebookQuoteResult = {
  subtotal: number;
  basePriceAmount: number;
  baseDurationMinutes: number;
  hourlyRateCentsPerMaidHour: number;
  discount: number;
  tax: number;
  total: number;
  tip: number;
  deposit: number;
  amountDueNow: number;
  pricingDisplayMode: PricebookPricingDisplayMode;
  durationMinutes: number;
  serviceName: string;
  optionNames: string[];
  optionDetails: PricebookQuoteOptionDetail[];
  promotionCode: string | null;
  promoStatus: string | null;
  promoMessage: string | null;
  frequencyRuleName: string | null;
  frequencyDiscount: number;
  arrivalFlexibilityEligible: boolean;
  arrivalFlexibilitySelected: boolean;
  arrivalFlexibilityLabel: string;
  arrivalFlexibilityDiscount: number;
  customerSuppliesEquipmentSelected: boolean;
  customerSuppliesEquipmentDiscount: number;
  customerSuppliesEquipmentRequiredItems: string[];
  promoDiscount: number;
  scenarioCode: string;
  scenarioName: string;
  apartmentFlatFeePackageCode: string | null;
  apartmentFlatFeePackageName: string | null;
  scenarioPricingMode: string;
  scenarioCustomerNote: string;
  remodelingOptionCode: string | null;
  remodelingOptionLabel: string | null;
  remodelingHourlyRatePerMaidHour: number;
  remodelingMinimumMaidCount: number;
  remodelingMinimumHours: number;
  remodelingMinimumLaborHours: number;
  remodelingStartingAtAmount: number;
  skipAreaDeductionAmount: number;
  skipAreaDurationDeductionMinutes: number;
  skipAreaDeductionRows: SkipAreaDeductionRow[];
};

type QuoteComputationInput = {
  baseAmount: number;
  optionAmounts?: number[];
  durationMinutes: number;
  optionDurationMinutes?: number[];
  taxRate?: number;
  depositRate?: number;
};

type RemodelingPricingRule = {
  optionCode: string;
  optionLabel: string;
  hourlyRatePerMaidHour: number;
  minimumMaidCount: number;
  minimumHours: number;
  active: boolean;
};

type ResidentialFormulaQuoteOverride = {
  bucket: ResidentialFormulaBucket;
  serviceType: ResidentialServiceType;
  serviceLabel: string;
  baseTeamMinutes: number;
  finalTeamMinutes: number;
  durationMinutes: number;
  basePriceCents: number;
  hourlyRateCentsPerMaidHour: number;
  conditionMultiplier: number;
  skipAreaDeductionAmount: number;
  skipAreaDurationDeductionMinutes: number;
  skipAreaDeductionRows: SkipAreaDeductionRow[];
};

type BuildingCommonAreasQuoteOverride = {
  bucket: BuildingCommonAreasBucket;
  label: string;
  pricingMode: BuildingCommonAreasPricingMode;
  basePriceCents: number;
  baseMinutes: number;
  minimumChargeCents: number;
};

type OfficeCommercialQuoteOverride = {
  pricingMode: "instant" | "manual";
  baseTeamMinutes: number;
  finalTeamMinutes: number;
  durationMinutes: number;
  basePriceCents: number;
  hourlyRateCentsPerMaidHour: number;
  manualReviewReason: string;
};

const defaultArrivalFlexibilitySettings = {
  enabled: false,
  startTime: "07:30",
  endTime: "15:30",
  discountType: "fixed" as const,
  discountValue: 0,
  excludedWindowStartTimes: ["07:30"]
};

export const defaultBuildingCommonAreasMinimumChargeCents = 9600;

export const defaultBuildingCommonAreasPricingRules: BuildingCommonAreasPricingRule[] = [
  { bucket: "1_3", label: "1-3 apartments", pricingMode: "instant", basePriceCents: 9600, baseMinutes: 60, displayOrder: 0 },
  { bucket: "4_6", label: "4-6 apartments", pricingMode: "instant", basePriceCents: 12000, baseMinutes: 75, displayOrder: 1 },
  { bucket: "7_9", label: "7-9 apartments", pricingMode: "instant", basePriceCents: 14400, baseMinutes: 90, displayOrder: 2 },
  { bucket: "10_12", label: "10-12 apartments", pricingMode: "instant", basePriceCents: 16800, baseMinutes: 105, displayOrder: 3 },
  { bucket: "13_15", label: "13-15 apartments", pricingMode: "instant", basePriceCents: 19200, baseMinutes: 120, displayOrder: 4 },
  { bucket: "over_15", label: "Larger than 15 apartments", pricingMode: "manual", basePriceCents: 0, baseMinutes: 0, displayOrder: 5 }
];

export const defaultResidentialFormulaRules: ResidentialFormulaRule[] = [
  {
    bucket: "apartment_like",
    label: "Apartment / condo",
    baseTeamMinutes: 30,
    minutesPerSquareFoot: 0.0405,
    minutesPerBedroom: 7.5,
    minutesPerFullBathroom: 15,
    minutesPerHalfBathroom: 7.5,
    minutesPerAdditionalLevel: 7.5
  },
  {
    bucket: "townhouse",
    label: "Townhouse",
    baseTeamMinutes: 0,
    minutesPerSquareFoot: 0.035,
    minutesPerBedroom: 7.5,
    minutesPerFullBathroom: 15,
    minutesPerHalfBathroom: 7.5,
    minutesPerAdditionalLevel: 15
  },
  {
    bucket: "single_family",
    label: "Single-family house",
    baseTeamMinutes: 0,
    minutesPerSquareFoot: 0.0375,
    minutesPerBedroom: 15,
    minutesPerFullBathroom: 15,
    minutesPerHalfBathroom: 7.5,
    minutesPerAdditionalLevel: 15
  }
];

export const defaultResidentialComponentFormulaRules: ResidentialComponentFormulaRule[] = [
  {
    bucket: "apartment_like",
    label: "Apartment / condo",
    setupTeamMinutes: 15,
    kitchenTeamMinutes: 30,
    minutesPerSquareFoot: 0.025,
    minutesPerBedroom: 8,
    minutesPerFullBathroom: 18,
    minutesPerHalfBathroom: 9,
    minutesPerExtraHalfLevel: 5
  },
  {
    bucket: "townhouse",
    label: "Townhouse",
    setupTeamMinutes: 25,
    kitchenTeamMinutes: 30,
    minutesPerSquareFoot: 0.024,
    minutesPerBedroom: 8,
    minutesPerFullBathroom: 18,
    minutesPerHalfBathroom: 9,
    minutesPerExtraHalfLevel: 7.5
  },
  {
    bucket: "single_family",
    label: "Single-family house",
    setupTeamMinutes: 25,
    kitchenTeamMinutes: 30,
    minutesPerSquareFoot: 0.026,
    minutesPerBedroom: 12,
    minutesPerFullBathroom: 18,
    minutesPerHalfBathroom: 9,
    minutesPerExtraHalfLevel: 7.5
  }
];

export const defaultResidentialHourlyServiceProfiles: ResidentialHourlyServiceProfile[] = [
  {
    serviceType: "standard_residential_cleaning",
    label: "Basic Cleaning",
    hourlyRateCentsPerMaidHour: 4800,
    minimumTeamMinutes: 120,
    serviceTimeMultiplier: 1
  },
  {
    serviceType: "deep_cleaning",
    label: "Deep Cleaning",
    hourlyRateCentsPerMaidHour: 5100,
    minimumTeamMinutes: 120,
    serviceTimeMultiplier: 1
  },
  {
    serviceType: "move_in_cleaning",
    label: "Move-In / Move-Out Cleaning",
    hourlyRateCentsPerMaidHour: 5300,
    minimumTeamMinutes: 120,
    serviceTimeMultiplier: 1
  },
  {
    serviceType: "move_out_cleaning",
    label: "Move-In / Move-Out Cleaning",
    hourlyRateCentsPerMaidHour: 5300,
    minimumTeamMinutes: 120,
    serviceTimeMultiplier: 1
  }
];

export const defaultResidentialConditionAdjustmentPercentages: ResidentialConditionAdjustmentPercentages = {
  kitchen: { 1: 10, 2: 5, 3: 0, 4: -5, 5: -10 },
  bathrooms: { 1: 10, 2: 5, 3: 0, 4: -5, 5: -10 },
  bedrooms: { 1: 5, 2: 3, 3: 0, 4: -3, 5: -5 },
  otherAreas: { 1: 5, 2: 3, 3: 0, 4: -3, 5: -5 }
};

export const defaultResidentialComponentConditionMultipliers: ResidentialComponentConditionMultipliers = {
  kitchen: { 1: 1.2, 2: 1.1, 3: 1, 4: 0.95, 5: 0.9 },
  bathrooms: { 1: 1.2, 2: 1.1, 3: 1, 4: 0.95, 5: 0.9 },
  bedrooms: { 1: 1.1, 2: 1.05, 3: 1, 4: 0.97, 5: 0.94 },
  otherAreas: { 1: 1.1, 2: 1.05, 3: 1, 4: 0.97, 5: 0.94 }
};

export const defaultResidentialFormulaLevelSquareFootageMinimums: ResidentialFormulaLevelSquareFootageMinimum[] = [
  { levels: 1, minimumSquareFeet: 1400 },
  { levels: 1.5, minimumSquareFeet: 1600 },
  { levels: 2, minimumSquareFeet: 1800 },
  { levels: 2.5, minimumSquareFeet: 2000 },
  { levels: 3, minimumSquareFeet: 2200 }
];

export const defaultResidentialFormulaMissingHalfLevelIncrement = 200;

export const defaultOfficeCommercialFormula: OfficeCommercialFormulaRule = {
  hourlyRateCentsPerMaidHour: 5100,
  minimumTeamMinutes: 120,
  baseTeamMinutes: 30,
  minutesPerSquareFoot: 0.025,
  minutesPerWorkstation: 3,
  minutesPerPrivateRoom: 8,
  minutesPerRestroom: 15,
  minutesPerBreakroom: 12,
  minutesPerAdditionalLevel: 10,
  manualReviewSquareFeetMax: 6000
};

export const defaultResidentialSkipAreaDurationRules: ResidentialSkipAreaDurationRule[] = [
  { propertyGroup: "apartment_condo", areaCode: "kitchen", label: "Kitchen", laborMinutes: 30, displayOrder: 0 },
  { propertyGroup: "apartment_condo", areaCode: "full_bathroom", label: "Full bathroom", laborMinutes: 15, displayOrder: 1 },
  { propertyGroup: "apartment_condo", areaCode: "half_bathroom", label: "Half bathroom", laborMinutes: 7.5, displayOrder: 2 },
  { propertyGroup: "apartment_condo", areaCode: "bedroom", label: "Bedroom / office / den", laborMinutes: 7.5, displayOrder: 3 },
  {
    propertyGroup: "apartment_condo",
    areaCode: "living_areas",
    label: "Living areas, up to 1,800 sqft",
    laborMinutes: 7.5,
    squareFootageMax: 1800,
    displayOrder: 4
  },
  {
    propertyGroup: "apartment_condo",
    areaCode: "living_areas",
    label: "Living areas, over 1,800 sqft",
    laborMinutes: 15,
    squareFootageMin: 1801,
    displayOrder: 5
  },
  { propertyGroup: "apartment_condo", areaCode: "living_room", label: "Living room", laborMinutes: 7.5, displayOrder: 6 },
  { propertyGroup: "apartment_condo", areaCode: "dining_room", label: "Dining room", laborMinutes: 7.5, displayOrder: 7 },
  { propertyGroup: "apartment_condo", areaCode: "hallways", label: "Hallways", laborMinutes: 7.5, displayOrder: 8 },
  { propertyGroup: "house", areaCode: "kitchen", label: "Kitchen", laborMinutes: 30, displayOrder: 20 },
  { propertyGroup: "house", areaCode: "full_bathroom", label: "Full bathroom", laborMinutes: 15, displayOrder: 21 },
  { propertyGroup: "house", areaCode: "half_bathroom", label: "Half bathroom", laborMinutes: 7.5, displayOrder: 22 },
  { propertyGroup: "house", areaCode: "bedroom", label: "Bedroom / office / den", laborMinutes: 7.5, displayOrder: 23 },
  { propertyGroup: "house", areaCode: "living_areas", label: "Living areas", laborMinutes: 7.5, displayOrder: 24 },
  { propertyGroup: "house", areaCode: "living_room", label: "Living room", laborMinutes: 7.5, displayOrder: 25 },
  { propertyGroup: "house", areaCode: "dining_room", label: "Dining room", laborMinutes: 7.5, displayOrder: 26 },
  { propertyGroup: "house", areaCode: "hallways", label: "Hallways", laborMinutes: 15, displayOrder: 27 }
];

const remodelingOptionLabelsByCode: Record<string, string> = {
  new_construction_heavy: "New construction (heavy debris present)",
  new_construction_precleaned: "New construction (pre-cleaned by construction company)",
  full_remodeling: "Full renovation",
  partial_remodeling: "Partial renovation",
  kitchen: "Kitchen",
  bathrooms: "Bathrooms",
  bedrooms: "Bedrooms",
  living_areas: "Living areas",
  floors_replacement: "Floors replacement",
  floors_refinishing: "Floors refinishing",
  walls_painting: "Walls painting",
  carpets_replacement: "Carpets replacement",
  workstations: "Workstations renovation",
  other: "Other"
};

const carpetUnitAdminDetailKeys = {
  rooms: "carpetRooms",
  hallways: "carpetHallways",
  steps: "carpetSteps",
  area_rugs: "carpetAreaRugs"
} as const;

type CarpetUnitCode = keyof typeof carpetUnitAdminDetailKeys;

type ApartmentFlatFeePackage = {
  packageCode: string;
  displayName: string;
  bedrooms: number;
  bathrooms: number;
  maxSquareFeet: number;
  maxLevels: number;
  basePriceCents: number;
  visitMinutes: number;
};

const apartmentFlatFeeDefaultCrewSize = 2;

const apartmentFlatFeePackages: ApartmentFlatFeePackage[] = [
  {
    packageCode: "studio-1ba",
    displayName: "Studio Apartment",
    bedrooms: 0,
    bathrooms: 1,
    maxSquareFeet: 1200,
    maxLevels: 1,
    basePriceCents: 12000,
    visitMinutes: 75
  },
  {
    packageCode: "1br-1ba",
    displayName: "1 Bedroom / 1 Bathroom",
    bedrooms: 1,
    bathrooms: 1,
    maxSquareFeet: 1200,
    maxLevels: 1,
    basePriceCents: 14400,
    visitMinutes: 90
  },
  {
    packageCode: "2br-1ba",
    displayName: "2 Bedrooms / 1 Bathroom",
    bedrooms: 2,
    bathrooms: 1,
    maxSquareFeet: 1200,
    maxLevels: 1,
    basePriceCents: 16800,
    visitMinutes: 105
  },
  {
    packageCode: "1br-2ba",
    displayName: "1 Bedroom / 2 Bathrooms",
    bedrooms: 1,
    bathrooms: 2,
    maxSquareFeet: 1200,
    maxLevels: 1,
    basePriceCents: 16800,
    visitMinutes: 105
  },
  {
    packageCode: "2br-2ba",
    displayName: "2 Bedrooms / 2 Bathrooms",
    bedrooms: 2,
    bathrooms: 2,
    maxSquareFeet: 1200,
    maxLevels: 1,
    basePriceCents: 19200,
    visitMinutes: 120
  },
  {
    packageCode: "3br-1ba",
    displayName: "3 Bedrooms / 1 Bathroom",
    bedrooms: 3,
    bathrooms: 1,
    maxSquareFeet: 1200,
    maxLevels: 1,
    basePriceCents: 19200,
    visitMinutes: 120
  }
];

export const defaultPricebookSettings: PricebookSettings = {
  version: 1,
  advancedRules: {
    buildingCommonAreasMinimumChargeCents: defaultBuildingCommonAreasMinimumChargeCents,
    buildingCommonAreasPricingRules: defaultBuildingCommonAreasPricingRules,
    residentialFormulaRules: defaultResidentialFormulaRules,
    residentialComponentFormulaRules: defaultResidentialComponentFormulaRules,
    residentialHourlyServiceProfiles: defaultResidentialHourlyServiceProfiles,
    residentialConditionAdjustmentPercentages: defaultResidentialConditionAdjustmentPercentages,
    residentialComponentConditionMultipliers: defaultResidentialComponentConditionMultipliers,
    residentialSkipAreaDurationRules: defaultResidentialSkipAreaDurationRules,
    residentialFormulaLevelSquareFootageMinimums: defaultResidentialFormulaLevelSquareFootageMinimums,
    residentialFormulaMissingHalfLevelIncrement: defaultResidentialFormulaMissingHalfLevelIncrement,
    officeCommercialFormula: defaultOfficeCommercialFormula
  }
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toFiniteNumber(value: unknown, fallback: number) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function normalizeCents(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : 0;
}

function normalizeActive(value: unknown, fallback = true) {
  if (value == null) {
    return fallback;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  const normalized = String(value).trim().toLowerCase();
  return !["0", "false", "no", "inactive"].includes(normalized);
}

function normalizeIdentifier(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return normalizeStringList(parsed);
        }
      } catch {
        // Fall through to loose splitting.
      }
    }
    return trimmed.split(/[,\n|]/).map((item) => item.trim()).filter(Boolean);
  }

  return [];
}

function calculateBaseQuote(input: QuoteComputationInput) {
  const subtotal = input.baseAmount + (input.optionAmounts ?? []).reduce((sum, value) => sum + value, 0);
  const taxableAmount = Math.max(0, subtotal);
  const tax = Math.round(taxableAmount * (input.taxRate ?? 0));
  const total = taxableAmount + tax;
  const deposit = Math.round(total * (input.depositRate ?? 0));
  const durationMinutes =
    input.durationMinutes + (input.optionDurationMinutes ?? []).reduce((sum, value) => sum + value, 0);

  return { subtotal, tax, total, deposit, amountDueNow: deposit, durationMinutes };
}

function calculateDiscountAmount(amountCents: number, type: string, value: unknown) {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  if (type === "fixed") {
    return Math.min(amountCents, Math.round(numericValue));
  }

  if (type === "percentage") {
    return Math.min(amountCents, Math.round(amountCents * (numericValue / 100)));
  }

  return 0;
}

function normalizeCustomerSuppliesProvidedBy(adminDetails: Record<string, string>) {
  const value = String(
    adminDetails.supplies_provided_by ??
      adminDetails.customer_provides_supplies_equipment ??
      adminDetails.customerProvidesSuppliesChoice ??
      ""
  )
    .trim()
    .toLowerCase();

  return ["customer", "yes", "true", "1"].includes(value);
}

function normalizeCustomerSuppliesEquipmentDiscountCents(settings: CustomerSuppliesEquipmentQuoteSettings | null | undefined) {
  const amount = Number(settings?.discountAmount ?? 0);
  return Number.isFinite(amount) ? Math.max(0, Math.round(amount * 100)) : 0;
}

export function calculateBuildingCommonAreasFrequencyDiscount(input: {
  baseAmountCents: number;
  minimumChargeCents?: number;
  discountType: string;
  discountValue: unknown;
}) {
  const baseAmountCents = normalizeCents(input.baseAmountCents);
  const requestedDiscount = calculateDiscountAmount(baseAmountCents, input.discountType, input.discountValue);
  const minimumChargeCents = normalizeBuildingCommonAreasMinimumChargeCents(input.minimumChargeCents);
  const maxDiscount = Math.max(0, baseAmountCents - minimumChargeCents);
  return Math.min(requestedDiscount, maxDiscount);
}

function normalizeBuildingCommonAreasPricingMode(
  value: unknown,
  fallback: BuildingCommonAreasPricingMode
): BuildingCommonAreasPricingMode {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[^a-z]+/g, "_").replace(/^_+|_+$/g, "");
  if (normalized === "manual" || normalized === "manual_review") {
    return "manual";
  }
  if (normalized === "instant" || normalized === "exact") {
    return "instant";
  }
  return fallback;
}

export function normalizeBuildingApartmentsBucket(value: unknown): BuildingCommonAreasBucket | "" {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  switch (normalized) {
    case "1_3":
    case "1to3":
    case "1_3_apartments":
    case "1to3_apartments":
      return "1_3";
    case "4_6":
    case "4to6":
    case "4_6_apartments":
    case "4to6_apartments":
      return "4_6";
    case "7_9":
    case "7to9":
    case "7_9_apartments":
    case "7to9_apartments":
      return "7_9";
    case "10_12":
    case "10to12":
    case "10_12_apartments":
    case "10to12_apartments":
      return "10_12";
    case "13_15":
    case "13to15":
    case "13_15_apartments":
    case "13to15_apartments":
    case "13_24":
    case "13to24":
    case "13_24_apartments":
    case "13to24_apartments":
      return "13_15";
    case "over_15":
    case "over15":
    case "larger_than_15":
    case "larger_than_15_apartments":
    case "16_plus":
    case "16plus":
    case "24":
    case "24_plus":
    case "24plus":
    case "24_apartments":
    case "24_plus_apartments":
    case "24plus_apartments":
      return "over_15";
    default:
      return "";
  }
}

export function normalizeBuildingCommonAreasMinimumChargeCents(value: unknown) {
  return Math.max(0, Math.round(toFiniteNumber(value, defaultBuildingCommonAreasMinimumChargeCents)));
}

export function normalizeBuildingCommonAreasPricingRules(value: unknown): BuildingCommonAreasPricingRule[] {
  const provided = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => isRecord(entry))
    : [];
  const providedByBucket = new Map<BuildingCommonAreasBucket, Record<string, unknown>>();

  for (const entry of provided) {
    const bucket = normalizeBuildingApartmentsBucket(
      entry.bucket ?? entry.apartmentBucket ?? entry.buildingApartmentsBucket ?? entry.code ?? entry.label
    );
    if (bucket) {
      providedByBucket.set(bucket, entry);
    }
  }

  return defaultBuildingCommonAreasPricingRules.map((fallbackRule) => {
    const source = providedByBucket.get(fallbackRule.bucket);
    const label = String(source?.label ?? source?.apartmentLabel ?? fallbackRule.label).trim();
    return {
      bucket: fallbackRule.bucket,
      label: label || fallbackRule.label,
      pricingMode: normalizeBuildingCommonAreasPricingMode(source?.pricingMode, fallbackRule.pricingMode),
      basePriceCents: normalizeCents(source?.basePriceCents ?? source?.basePriceAmount ?? fallbackRule.basePriceCents),
      baseMinutes: Math.max(0, Math.round(toFiniteNumber(source?.baseMinutes ?? source?.durationMinutes, fallbackRule.baseMinutes))),
      displayOrder: Number(source?.displayOrder ?? fallbackRule.displayOrder)
    };
  });
}

function normalizeResidentialFormulaBucket(value: unknown): ResidentialFormulaBucket | null {
  const normalized = normalizeIdentifier(value);
  switch (normalized) {
    case "apartment":
    case "apartment_condo":
    case "apartment_like":
    case "apartment_under_1200":
    case "apartment_over_1200":
    case "condo":
      return "apartment_like";
    case "townhouse":
      return "townhouse";
    case "single_family":
    case "single_family_house":
    case "house":
      return "single_family";
    default:
      return null;
  }
}

export function normalizeResidentialFormulaRules(value: unknown): ResidentialFormulaRule[] {
  const provided = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => isRecord(entry))
    : isRecord(value)
      ? Object.values(value).filter((entry): entry is Record<string, unknown> => isRecord(entry))
      : [];
  const providedByBucket = new Map<ResidentialFormulaBucket, Record<string, unknown>>();

  for (const entry of provided) {
    const bucket = normalizeResidentialFormulaBucket(entry.bucket ?? entry.code ?? entry.propertyBucket ?? entry.label);
    if (bucket) {
      providedByBucket.set(bucket, entry);
    }
  }

  return defaultResidentialFormulaRules.map((fallbackRule) => {
    const source = providedByBucket.get(fallbackRule.bucket);
    const label = String(source?.label ?? fallbackRule.label).trim();
    return {
      bucket: fallbackRule.bucket,
      label: label || fallbackRule.label,
      baseTeamMinutes: Math.max(0, toFiniteNumber(source?.baseTeamMinutes, fallbackRule.baseTeamMinutes)),
      minutesPerSquareFoot: Math.max(0, toFiniteNumber(source?.minutesPerSquareFoot, fallbackRule.minutesPerSquareFoot)),
      minutesPerBedroom: Math.max(0, toFiniteNumber(source?.minutesPerBedroom, fallbackRule.minutesPerBedroom)),
      minutesPerFullBathroom: Math.max(0, toFiniteNumber(source?.minutesPerFullBathroom, fallbackRule.minutesPerFullBathroom)),
      minutesPerHalfBathroom: Math.max(0, toFiniteNumber(source?.minutesPerHalfBathroom, fallbackRule.minutesPerHalfBathroom)),
      minutesPerAdditionalLevel: Math.max(0, toFiniteNumber(source?.minutesPerAdditionalLevel, fallbackRule.minutesPerAdditionalLevel))
    };
  });
}

export function normalizeResidentialComponentFormulaRules(value: unknown): ResidentialComponentFormulaRule[] {
  const provided = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => isRecord(entry))
    : isRecord(value)
      ? Object.values(value).filter((entry): entry is Record<string, unknown> => isRecord(entry))
      : [];
  const providedByBucket = new Map<ResidentialFormulaBucket, Record<string, unknown>>();

  for (const entry of provided) {
    const bucket = normalizeResidentialFormulaBucket(entry.bucket ?? entry.code ?? entry.propertyBucket ?? entry.label);
    if (bucket) {
      providedByBucket.set(bucket, entry);
    }
  }

  return defaultResidentialComponentFormulaRules.map((fallbackRule) => {
    const source = providedByBucket.get(fallbackRule.bucket);
    const label = String(source?.label ?? fallbackRule.label).trim();
    return {
      bucket: fallbackRule.bucket,
      label: label || fallbackRule.label,
      setupTeamMinutes: Math.max(
        0,
        toFiniteNumber(
          source?.setupTeamMinutes ?? source?.setupMinutes ?? source?.baseSetupMinutes,
          fallbackRule.setupTeamMinutes
        )
      ),
      kitchenTeamMinutes: Math.max(
        0,
        toFiniteNumber(source?.kitchenTeamMinutes ?? source?.kitchenMinutes, fallbackRule.kitchenTeamMinutes)
      ),
      minutesPerSquareFoot: Math.max(0, toFiniteNumber(source?.minutesPerSquareFoot, fallbackRule.minutesPerSquareFoot)),
      minutesPerBedroom: Math.max(0, toFiniteNumber(source?.minutesPerBedroom, fallbackRule.minutesPerBedroom)),
      minutesPerFullBathroom: Math.max(0, toFiniteNumber(source?.minutesPerFullBathroom, fallbackRule.minutesPerFullBathroom)),
      minutesPerHalfBathroom: Math.max(0, toFiniteNumber(source?.minutesPerHalfBathroom, fallbackRule.minutesPerHalfBathroom)),
      minutesPerExtraHalfLevel: Math.max(
        0,
        toFiniteNumber(
          source?.minutesPerExtraHalfLevel ?? source?.minutesPerHalfLevel,
          fallbackRule.minutesPerExtraHalfLevel
        )
      )
    };
  });
}

function normalizeResidentialServiceType(value: unknown): ResidentialServiceType | null {
  const normalized = normalizeIdentifier(value);
  switch (normalized) {
    case "basic":
    case "basic_cleaning":
    case "standard":
    case "standard_cleaning":
    case "standard_residential_cleaning":
      return "standard_residential_cleaning";
    case "deep":
    case "deep_cleaning":
      return "deep_cleaning";
    case "move_in":
    case "move_in_cleaning":
      return "move_in_cleaning";
    case "move_out":
    case "move_out_cleaning":
    case "move_in_move_out":
    case "move_in_move_out_cleaning":
      return "move_out_cleaning";
    default:
      return null;
  }
}

export function normalizeResidentialHourlyServiceProfiles(value: unknown): ResidentialHourlyServiceProfile[] {
  const provided = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => isRecord(entry))
    : isRecord(value)
      ? Object.values(value).filter((entry): entry is Record<string, unknown> => isRecord(entry))
      : [];
  const providedByServiceType = new Map<ResidentialServiceType, Record<string, unknown>>();

  for (const entry of provided) {
    const serviceType = normalizeResidentialServiceType(entry.serviceType ?? entry.code ?? entry.type ?? entry.label);
    if (serviceType) {
      providedByServiceType.set(serviceType, entry);
    }
  }

  return defaultResidentialHourlyServiceProfiles.map((fallbackProfile) => {
    const source = providedByServiceType.get(fallbackProfile.serviceType);
    const label = String(source?.label ?? fallbackProfile.label).trim();
    return {
      serviceType: fallbackProfile.serviceType,
      label: label || fallbackProfile.label,
      hourlyRateCentsPerMaidHour: normalizeCents(
        source?.hourlyRateCentsPerMaidHour ?? source?.hourlyRateCents ?? fallbackProfile.hourlyRateCentsPerMaidHour
      ),
      minimumTeamMinutes: Math.max(0, Math.round(toFiniteNumber(source?.minimumTeamMinutes, fallbackProfile.minimumTeamMinutes))),
      serviceTimeMultiplier: Math.max(0, toFiniteNumber(source?.serviceTimeMultiplier, fallbackProfile.serviceTimeMultiplier))
    };
  });
}

function normalizeConditionAdjustmentMap(
  value: unknown,
  fallback: Record<number, number>
): Record<number, number> {
  const source = isRecord(value) ? value : {};
  const normalized: Record<number, number> = {};

  for (const score of [1, 2, 3, 4, 5]) {
    const rawValue = source[score] ?? source[String(score)];
    normalized[score] = toFiniteNumber(rawValue, fallback[score] ?? 0);
  }

  return normalized;
}

export function normalizeResidentialConditionAdjustmentPercentages(
  value: unknown
): ResidentialConditionAdjustmentPercentages {
  const source = isRecord(value) ? value : {};
  return {
    kitchen: normalizeConditionAdjustmentMap(source.kitchen, defaultResidentialConditionAdjustmentPercentages.kitchen),
    bathrooms: normalizeConditionAdjustmentMap(source.bathrooms, defaultResidentialConditionAdjustmentPercentages.bathrooms),
    bedrooms: normalizeConditionAdjustmentMap(source.bedrooms, defaultResidentialConditionAdjustmentPercentages.bedrooms),
    otherAreas: normalizeConditionAdjustmentMap(source.otherAreas, defaultResidentialConditionAdjustmentPercentages.otherAreas)
  };
}

function normalizeConditionMultiplierMap(
  value: unknown,
  fallback: Record<number, number>
): Record<number, number> {
  const source = isRecord(value) ? value : {};
  const normalized: Record<number, number> = {};

  for (const score of [1, 2, 3, 4, 5]) {
    const rawValue = source[score] ?? source[String(score)];
    normalized[score] = Math.max(0, toFiniteNumber(rawValue, fallback[score] ?? 1));
  }

  return normalized;
}

export function normalizeResidentialComponentConditionMultipliers(
  value: unknown
): ResidentialComponentConditionMultipliers {
  const source = isRecord(value) ? value : {};
  return {
    kitchen: normalizeConditionMultiplierMap(source.kitchen, defaultResidentialComponentConditionMultipliers.kitchen),
    bathrooms: normalizeConditionMultiplierMap(source.bathrooms, defaultResidentialComponentConditionMultipliers.bathrooms),
    bedrooms: normalizeConditionMultiplierMap(source.bedrooms, defaultResidentialComponentConditionMultipliers.bedrooms),
    otherAreas: normalizeConditionMultiplierMap(source.otherAreas, defaultResidentialComponentConditionMultipliers.otherAreas)
  };
}

function normalizeResidentialSkipAreaPropertyGroup(value: unknown): ResidentialSkipAreaPropertyGroup | null {
  const normalized = normalizeIdentifier(value);
  switch (normalized) {
    case "apartment":
    case "condo":
    case "apartment_condo":
    case "apartment_like":
      return "apartment_condo";
    case "house":
    case "home":
    case "townhouse":
    case "single_family":
    case "single_family_house":
      return "house";
    default:
      return null;
  }
}

function normalizeResidentialSkipAreaDurationAreaCode(value: unknown): ResidentialSkipAreaDurationAreaCode | null {
  const normalized = normalizeIdentifier(value);
  switch (normalized) {
    case "kitchen":
      return "kitchen";
    case "full_bathroom":
    case "full_bathrooms":
    case "bathroom":
    case "bathrooms":
      return "full_bathroom";
    case "half_bathroom":
    case "half_bathrooms":
    case "half_bath":
      return "half_bathroom";
    case "bedroom":
    case "bedrooms":
    case "bedrooms_offices_dens":
      return "bedroom";
    case "living_areas":
    case "living_area":
      return "living_areas";
    case "living_room":
      return "living_room";
    case "dining_room":
      return "dining_room";
    case "hallways":
    case "hallway":
      return "hallways";
    default:
      return null;
  }
}

function residentialSkipAreaDurationRuleKey(rule: Pick<ResidentialSkipAreaDurationRule, "propertyGroup" | "areaCode" | "squareFootageMin" | "squareFootageMax">) {
  return [
    rule.propertyGroup,
    rule.areaCode,
    rule.squareFootageMin == null ? "" : String(rule.squareFootageMin),
    rule.squareFootageMax == null ? "" : String(rule.squareFootageMax)
  ].join(":");
}

function normalizeResidentialSkipAreaDurationRule(
  entry: Record<string, unknown>,
  fallback?: ResidentialSkipAreaDurationRule,
  fallbackOrder = 0
): ResidentialSkipAreaDurationRule | null {
  const propertyGroup = normalizeResidentialSkipAreaPropertyGroup(
    entry.propertyGroup ?? entry.propertyType ?? entry.propertyGroupCode ?? fallback?.propertyGroup
  );
  const areaCode = normalizeResidentialSkipAreaDurationAreaCode(
    entry.areaCode ?? entry.skipAreaCode ?? entry.code ?? entry.area ?? fallback?.areaCode
  );
  if (!propertyGroup || !areaCode) {
    return null;
  }

  const squareFootageMinRaw = entry.squareFootageMin ?? entry.minSquareFeet ?? entry.squareFeetMin ?? fallback?.squareFootageMin;
  const squareFootageMaxRaw = entry.squareFootageMax ?? entry.maxSquareFeet ?? entry.squareFeetMax ?? fallback?.squareFootageMax;
  const squareFootageMin = squareFootageMinRaw == null || squareFootageMinRaw === ""
    ? undefined
    : Math.max(0, Math.round(toFiniteNumber(squareFootageMinRaw, 0)));
  const squareFootageMax = squareFootageMaxRaw == null || squareFootageMaxRaw === ""
    ? undefined
    : Math.max(0, Math.round(toFiniteNumber(squareFootageMaxRaw, 0)));
  const label = String(entry.label ?? fallback?.label ?? "").trim();
  return {
    propertyGroup,
    areaCode,
    label: label || fallback?.label || areaCode,
    laborMinutes: Math.max(0, toFiniteNumber(entry.laborMinutes ?? entry.oneMaidMinutes ?? entry.minutes, fallback?.laborMinutes ?? 0)),
    ...(squareFootageMin == null ? {} : { squareFootageMin }),
    ...(squareFootageMax == null ? {} : { squareFootageMax }),
    displayOrder: Math.round(toFiniteNumber(entry.displayOrder, fallback?.displayOrder ?? fallbackOrder))
  };
}

export function normalizeResidentialSkipAreaDurationRules(value: unknown): ResidentialSkipAreaDurationRule[] {
  const sourceRows = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => isRecord(entry))
    : defaultResidentialSkipAreaDurationRules.map((entry) => entry as unknown as Record<string, unknown>);

  return sourceRows
    .map((entry, index) => normalizeResidentialSkipAreaDurationRule(entry, undefined, index))
    .filter((entry): entry is ResidentialSkipAreaDurationRule => entry !== null)
    .sort((left, right) => {
      const orderDelta = left.displayOrder - right.displayOrder;
      if (orderDelta !== 0) {
        return orderDelta;
      }
      return residentialSkipAreaDurationRuleKey(left).localeCompare(residentialSkipAreaDurationRuleKey(right));
    });
}

function normalizeHalfLevel(value: unknown) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.max(1, Math.ceil(numeric * 2) / 2) : 1;
}

export function normalizeResidentialFormulaLevelSquareFootageMinimums(
  value: unknown
): ResidentialFormulaLevelSquareFootageMinimum[] {
  const source = Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => isRecord(entry))
    : value == null
      ? defaultResidentialFormulaLevelSquareFootageMinimums.map((entry) => entry as unknown as Record<string, unknown>)
      : [];
  const rowsByLevel = new Map<number, ResidentialFormulaLevelSquareFootageMinimum>();

  for (const entry of source) {
    const levels = normalizeHalfLevel(entry.levels ?? entry.level ?? entry.levelCount);
    const minimumSquareFeet = Math.max(
      0,
      Math.round(toFiniteNumber(entry.minimumSquareFeet ?? entry.minimumBillableSquareFeet ?? entry.squareFeet, 0))
    );
    if (minimumSquareFeet > 0) {
      rowsByLevel.set(levels, { levels, minimumSquareFeet });
    }
  }

  return [...rowsByLevel.values()].sort((left, right) => left.levels - right.levels);
}

export function normalizeResidentialFormulaMissingHalfLevelIncrement(value: unknown) {
  return Math.max(0, Math.round(toFiniteNumber(value, defaultResidentialFormulaMissingHalfLevelIncrement)));
}

export function normalizeOfficeCommercialFormula(value: unknown): OfficeCommercialFormulaRule {
  const source = isRecord(value) ? value : {};
  return {
    hourlyRateCentsPerMaidHour: normalizeCents(
      source.hourlyRateCentsPerMaidHour ?? source.hourlyRateCents ?? defaultOfficeCommercialFormula.hourlyRateCentsPerMaidHour
    ),
    minimumTeamMinutes: Math.max(
      0,
      Math.round(toFiniteNumber(source.minimumTeamMinutes, defaultOfficeCommercialFormula.minimumTeamMinutes))
    ),
    baseTeamMinutes: Math.max(0, toFiniteNumber(source.baseTeamMinutes, defaultOfficeCommercialFormula.baseTeamMinutes)),
    minutesPerSquareFoot: Math.max(0, toFiniteNumber(source.minutesPerSquareFoot, defaultOfficeCommercialFormula.minutesPerSquareFoot)),
    minutesPerWorkstation: Math.max(0, toFiniteNumber(source.minutesPerWorkstation, defaultOfficeCommercialFormula.minutesPerWorkstation)),
    minutesPerPrivateRoom: Math.max(0, toFiniteNumber(source.minutesPerPrivateRoom, defaultOfficeCommercialFormula.minutesPerPrivateRoom)),
    minutesPerRestroom: Math.max(0, toFiniteNumber(source.minutesPerRestroom, defaultOfficeCommercialFormula.minutesPerRestroom)),
    minutesPerBreakroom: Math.max(0, toFiniteNumber(source.minutesPerBreakroom, defaultOfficeCommercialFormula.minutesPerBreakroom)),
    minutesPerAdditionalLevel: Math.max(
      0,
      toFiniteNumber(source.minutesPerAdditionalLevel, defaultOfficeCommercialFormula.minutesPerAdditionalLevel)
    ),
    manualReviewSquareFeetMax: Math.max(
      0,
      Math.round(toFiniteNumber(source.manualReviewSquareFeetMax, defaultOfficeCommercialFormula.manualReviewSquareFeetMax))
    )
  };
}

function parseSquareFootageRange(value: string) {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const ascii = normalized.replace(/[\u2013\u2014]/g, "-").replace(/,/g, "");
  const match = ascii.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!match) {
    return null;
  }

  const min = Number(match[1]);
  const max = Number(match[2]);
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

function readSquareFootageMax(value: unknown) {
  const range = parseSquareFootageRange(String(value ?? ""));
  if (range) {
    return range.max;
  }
  return parseNumericToken(value);
}

function normalizeQuoteTeamSize(value: unknown) {
  const teamSize = Number(value);
  return Number.isFinite(teamSize) && teamSize > 0 ? Math.max(1, Math.round(teamSize)) : apartmentFlatFeeDefaultCrewSize;
}

function adjustVisitMinutesForTeamSize(defaultVisitMinutes: number, teamSize: number) {
  const laborMinutes = defaultVisitMinutes * apartmentFlatFeeDefaultCrewSize;
  return Math.max(0, Math.ceil(laborMinutes / Math.max(1, teamSize)));
}

function convertLaborMinutesToVisitMinutes(laborMinutes: number, teamSize: number) {
  return Math.max(0, Number(laborMinutes) || 0) / Math.max(1, teamSize);
}

function isBuildingCommonAreasScenario(scenario: PricebookRecord | null, service: PricebookRecord) {
  const questionProfile = String(scenario?.questionProfile ?? scenario?.question_profile ?? "").trim().toLowerCase();
  if (questionProfile === "building_common_areas") {
    return true;
  }
  const serviceName = String(service.name ?? "").trim().toLowerCase();
  return serviceName.includes("building common areas");
}

function isOfficeCommercialScenario(scenario: PricebookRecord | null, service: PricebookRecord) {
  const questionProfile = String(scenario?.questionProfile ?? scenario?.question_profile ?? "").trim().toLowerCase();
  if (questionProfile === "office_commercial") {
    return true;
  }

  const scenarioCode = normalizeIdentifier(scenario?.code ?? scenario?.name);
  if (scenarioCode === "office_commercial" || scenarioCode === "office_commercial_cleaning") {
    return true;
  }

  const serviceName = normalizeIdentifier(service.name);
  return serviceName.includes("office") || serviceName.includes("commercial");
}

function isApartmentFlatFeeScenario(scenario: PricebookRecord | null, service: PricebookRecord) {
  const scenarioCode = String(scenario?.code ?? "").trim().toLowerCase();
  if (scenarioCode === "apartment_under_1200") {
    return true;
  }

  const serviceName = String(service.name ?? "").trim().toLowerCase();
  return serviceName.includes("apartment") || serviceName.includes("condo");
}

function resolveApartmentFlatFeeQuoteOverride(
  service: PricebookRecord,
  scenario: PricebookRecord | null,
  adminDetails: Record<string, string>
) {
  if (!isApartmentFlatFeeScenario(scenario, service)) {
    return null;
  }

  const squareFootageMax = readSquareFootageMax(adminDetails.square_footage);
  const levels = parseNumericToken(adminDetails.levels);
  const bedrooms = parseNumericToken(adminDetails.bedrooms);
  const bathrooms = parseNumericToken(adminDetails.bathrooms);

  if (
    squareFootageMax == null ||
    squareFootageMax > 1200 ||
    levels == null ||
    levels > 1 ||
    bedrooms == null ||
    bathrooms == null
  ) {
    return null;
  }

  const normalizedBedrooms = Math.max(0, Math.round(bedrooms));
  const normalizedBathrooms = Math.max(0, Math.ceil(bathrooms));

  return apartmentFlatFeePackages.find((entry) => {
    return (
      entry.bedrooms === normalizedBedrooms &&
      entry.bathrooms === normalizedBathrooms &&
      squareFootageMax <= entry.maxSquareFeet &&
      levels <= entry.maxLevels
    );
  }) ?? null;
}

function resolveBuildingCommonAreasQuoteOverride(
  service: PricebookRecord,
  scenario: PricebookRecord | null,
  adminDetails: Record<string, string>
): BuildingCommonAreasQuoteOverride | null {
  if (!isBuildingCommonAreasScenario(scenario, service)) {
    return null;
  }

  const bucket = normalizeBuildingApartmentsBucket(adminDetails.building_apartments);
  if (!bucket) {
    return null;
  }

  const configuredRules = normalizeBuildingCommonAreasPricingRules(
    scenario?.buildingCommonAreasPricingRules ?? scenario?.building_common_areas_pricing_rules
  );
  const rule = configuredRules.find((entry) => entry.bucket === bucket);
  if (!rule) {
    return null;
  }

  return {
    bucket,
    label: rule.label,
    pricingMode: rule.pricingMode,
    basePriceCents: rule.pricingMode === "instant" ? rule.basePriceCents : 0,
    baseMinutes: rule.pricingMode === "instant" ? rule.baseMinutes : 0,
    minimumChargeCents: normalizeBuildingCommonAreasMinimumChargeCents(
      scenario?.buildingCommonAreasMinimumCharge ??
        scenario?.building_common_areas_minimum_charge ??
        defaultBuildingCommonAreasMinimumChargeCents
    )
  };
}

function getResidentialFormulaBucket(
  scenario: PricebookRecord | null,
  service: PricebookRecord
): ResidentialFormulaBucket | null {
  const scenarioBucket = normalizeResidentialFormulaBucket(
    scenario?.residentialFormulaBucket ?? scenario?.residential_formula_bucket ?? scenario?.code
  );
  if (scenarioBucket) {
    return scenarioBucket;
  }

  return normalizeResidentialFormulaBucket(service.residentialFormulaBucket ?? service.residential_formula_bucket ?? service.name);
}

function roundUpToNearest15(minutes: number) {
  return Math.max(15, Math.ceil(Math.max(0, minutes) / 15) * 15);
}

function readNumberFromDetails(adminDetails: Record<string, string>, keys: string[]) {
  for (const key of keys) {
    const value = adminDetails[key];
    if (String(value ?? "").trim()) {
      return parseNumericToken(value);
    }
  }
  return null;
}

function readOfficeCommercialCount(
  adminDetails: Record<string, string>,
  keys: string[],
  manualReviewThreshold: number
) {
  for (const key of keys) {
    const rawValue = String(adminDetails[key] ?? "").trim();
    if (!rawValue) {
      continue;
    }

    const normalized = normalizeIdentifier(rawValue);
    if (normalized.startsWith("over_")) {
      return { count: manualReviewThreshold + 1, requiresManualReview: true };
    }

    const range = rawValue.replace(/[\u2013\u2014]/g, "-").match(/(\d+)\s*-\s*(\d+)/);
    if (range) {
      const count = Number(range[2]);
      return {
        count: Number.isFinite(count) ? Math.max(0, Math.round(count)) : 0,
        requiresManualReview: Number.isFinite(count) ? count > manualReviewThreshold : false
      };
    }

    const count = parseNumericToken(rawValue);
    if (count != null) {
      const rounded = Math.max(0, Math.round(count));
      return { count: rounded, requiresManualReview: rounded > manualReviewThreshold };
    }
  }

  return null;
}

function readBathroomCounts(adminDetails: Record<string, string>) {
  const explicitFullBathrooms = readNumberFromDetails(adminDetails, ["full_bathrooms", "fullBathrooms"]);
  const explicitHalfBathrooms = readNumberFromDetails(adminDetails, ["half_bathrooms", "halfBathrooms"]);

  if ((explicitFullBathrooms ?? 0) > 0 || (explicitHalfBathrooms ?? 0) > 0) {
    return {
      fullBathrooms: Math.max(0, explicitFullBathrooms ?? 0),
      halfBathrooms: Math.max(0, explicitHalfBathrooms ?? 0)
    };
  }

  const bathrooms = readNumberFromDetails(adminDetails, ["bathrooms"]);
  if (bathrooms == null) {
    return null;
  }

  const normalizedBathrooms = Math.max(0, bathrooms);
  const fullBathrooms = Math.floor(normalizedBathrooms);
  const halfBathrooms = Math.max(0, Math.round((normalizedBathrooms - fullBathrooms) * 2));
  return { fullBathrooms, halfBathrooms };
}

function readJsonRecord(value: unknown): Record<string, unknown> | null {
  if (isRecord(value)) {
    return value;
  }
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeConditionScore(value: unknown) {
  const normalized = normalizeIdentifier(value);
  switch (normalized) {
    case "very_dirty":
    case "dirty":
      return 1;
    case "needs_work":
    case "needs_attention":
      return 2;
    case "balanced":
    case "average":
      return 3;
    case "fairly_clean":
      return 4;
    case "very_clean":
    case "clean":
      return 5;
    default: {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? Math.min(5, Math.max(1, Math.round(numeric))) : 3;
    }
  }
}

function readResidentialConditionAreas(adminDetails: Record<string, string>): Record<ResidentialConditionArea, number> {
  const rawAreas = readJsonRecord(adminDetails.condition_areas ?? adminDetails.conditionAreas);
  return {
    kitchen: normalizeConditionScore(rawAreas?.kitchen ?? adminDetails.kitchen_condition ?? adminDetails.kitchenCondition),
    bathrooms: normalizeConditionScore(rawAreas?.bathrooms ?? adminDetails.bathrooms_condition ?? adminDetails.bathroomsCondition),
    bedrooms: normalizeConditionScore(rawAreas?.bedrooms ?? adminDetails.bedrooms_condition ?? adminDetails.bedroomsCondition),
    otherAreas: normalizeConditionScore(
      rawAreas?.otherAreas ??
        rawAreas?.other_areas ??
        adminDetails.other_areas_condition ??
        adminDetails.otherAreasCondition ??
        adminDetails.living_areas_condition ??
        adminDetails.livingAreasCondition
    )
  };
}

function getResidentialConditionMultiplier(
  adminDetails: Record<string, string>,
  adjustmentPercentages: ResidentialConditionAdjustmentPercentages
) {
  const conditionAreas = readResidentialConditionAreas(adminDetails);
  const totalPercentAdjustment =
    (adjustmentPercentages.kitchen[conditionAreas.kitchen] ?? 0) +
    (adjustmentPercentages.bathrooms[conditionAreas.bathrooms] ?? 0) +
    (adjustmentPercentages.bedrooms[conditionAreas.bedrooms] ?? 0) +
    (adjustmentPercentages.otherAreas[conditionAreas.otherAreas] ?? 0);

  return Math.max(0.1, 1 + totalPercentAdjustment / 100);
}

const residentialMoveOutTaskCode = "move_in_move_out";

const nonDeepResidentialTaskCodes = new Set([
  "refrigerator_interior",
  "oven_interior",
  "dishes_hand_washing",
  "laundry_service",
  "carpet_shampooing",
  "vapor_steam_kitchen_bathrooms",
  "carpet_deodorizing",
  "bed_linens_change",
  "green_supplies",
  "rooms_freshener",
  "room_freshener",
  "pet_hair_clean_up",
  "antimicrobial_treatment",
  "move_in_move_out_supplies",
  "deep_cleaning_supplies"
]);

function getResidentialAdditionalTaskCodes(
  adminDetails: Record<string, string>,
  selectedOptions: PricebookRecord[]
) {
  const codes = [
    ...normalizeStringList(adminDetails.additional_tasks ?? adminDetails.additionalTasks),
    ...normalizeStringList(adminDetails.selected_extra_codes ?? adminDetails.selectedExtraCodes),
    ...normalizeStringList(adminDetails.extras),
    ...selectedOptions.flatMap((option) => {
      const configuredCode = option.estimatorExtraCode ?? option.estimator_extra_code ?? option.code;
      return configuredCode ? [String(configuredCode)] : [];
    })
  ];

  return codes
    .map((code) => normalizeIdentifier(code).replace(/_x\d+$/, ""))
    .filter(Boolean);
}

function deriveResidentialServiceType(
  adminDetails: Record<string, string>,
  selectedOptions: PricebookRecord[]
): ResidentialServiceType {
  const additionalTasks = getResidentialAdditionalTaskCodes(adminDetails, selectedOptions);
  if (additionalTasks.includes(residentialMoveOutTaskCode)) {
    return "move_out_cleaning";
  }

  const requestedServiceType = normalizeResidentialServiceType(
    adminDetails.requested_service_type ?? adminDetails.requestedServiceType
  );
  if (requestedServiceType && requestedServiceType !== "standard_residential_cleaning") {
    return requestedServiceType;
  }

  const hasDeepCleaningTask = additionalTasks.some((taskCode) => !nonDeepResidentialTaskCodes.has(taskCode));
  if (hasDeepCleaningTask) {
    return "deep_cleaning";
  }

  return requestedServiceType ?? "standard_residential_cleaning";
}

function getResidentialFormulaMinimumSquareFeetForLevel(
  levels: number,
  rows: ResidentialFormulaLevelSquareFootageMinimum[],
  missingHalfLevelIncrement: number
) {
  if (!rows.length) {
    return 0;
  }

  const normalizedLevels = normalizeHalfLevel(levels);
  const exact = rows.find((row) => row.levels === normalizedLevels);
  if (exact) {
    return exact.minimumSquareFeet;
  }

  const lowerRows = rows.filter((row) => row.levels < normalizedLevels);
  if (lowerRows.length) {
    const closestLower = lowerRows[lowerRows.length - 1];
    return closestLower.minimumSquareFeet + Math.round((normalizedLevels - closestLower.levels) * 2) * missingHalfLevelIncrement;
  }

  return rows[0]?.minimumSquareFeet ?? 0;
}

function resolveOfficeCommercialQuoteOverride(input: {
  catalog: PricebookCatalog;
  service: PricebookRecord;
  scenario: PricebookRecord | null;
  adminDetails: Record<string, string>;
  optionOneMaidMinutes: number;
  teamSize: number;
}): OfficeCommercialQuoteOverride | null {
  if (!isOfficeCommercialScenario(input.scenario, input.service)) {
    return null;
  }

  const advancedRules = input.catalog.settings?.advancedRules ?? {};
  const formulaRule = normalizeOfficeCommercialFormula(advancedRules.officeCommercialFormula);
  const squareFeet = readSquareFootageMax(input.adminDetails.square_footage);
  const workstations = readOfficeCommercialCount(input.adminDetails, ["workstations", "workstationsCount"], 30);
  const privateRooms = readOfficeCommercialCount(
    input.adminDetails,
    ["office_private_rooms", "privateOffices", "private_offices", "conferenceRooms", "conference_rooms"],
    10
  );
  const restrooms = readNumberFromDetails(input.adminDetails, ["restrooms", "bathrooms"]);
  const breakrooms = readOfficeCommercialCount(
    input.adminDetails,
    ["office_breakrooms", "breakRooms", "break_rooms", "breakrooms", "kitchenettes"],
    10
  );
  const levels = readNumberFromDetails(input.adminDetails, ["levels"]);

  if (
    squareFeet == null ||
    squareFeet <= 0 ||
    workstations == null ||
    privateRooms == null ||
    restrooms == null ||
    restrooms < 0 ||
    breakrooms == null ||
    levels == null ||
    levels < 1
  ) {
    return null;
  }

  const manualReviewReasons: string[] = [];
  if (workstations.requiresManualReview) {
    manualReviewReasons.push("workstations");
  }
  if (privateRooms.requiresManualReview) {
    manualReviewReasons.push("private rooms");
  }
  if (breakrooms.requiresManualReview) {
    manualReviewReasons.push("breakrooms / kitchenettes");
  }
  if (formulaRule.manualReviewSquareFeetMax > 0 && squareFeet > formulaRule.manualReviewSquareFeetMax) {
    manualReviewReasons.push("square footage");
  }

  if (manualReviewReasons.length) {
    return {
      pricingMode: "manual",
      baseTeamMinutes: 0,
      finalTeamMinutes: 0,
      durationMinutes: 0,
      basePriceCents: 0,
      hourlyRateCentsPerMaidHour: formulaRule.hourlyRateCentsPerMaidHour,
      manualReviewReason: `Office / commercial quote needs manual review because of ${manualReviewReasons.join(", ")}.`
    };
  }

  const normalizedLevels = normalizeHalfLevel(levels);
  const rawTeamMinutes =
    formulaRule.baseTeamMinutes +
    squareFeet * formulaRule.minutesPerSquareFoot +
    workstations.count * formulaRule.minutesPerWorkstation +
    privateRooms.count * formulaRule.minutesPerPrivateRoom +
    Math.ceil(Math.max(0, restrooms)) * formulaRule.minutesPerRestroom +
    breakrooms.count * formulaRule.minutesPerBreakroom +
    Math.max(normalizedLevels - 1, 0) * formulaRule.minutesPerAdditionalLevel;
  const roundedBaseTeamMinutes = Math.max(formulaRule.minimumTeamMinutes, roundUpToNearest15(rawTeamMinutes));
  const finalTeamMinutes = roundUpToNearest15(roundedBaseTeamMinutes + Math.max(0, input.optionOneMaidMinutes) / 2);
  const basePriceCents = Math.round(
    (roundedBaseTeamMinutes / 60) * apartmentFlatFeeDefaultCrewSize * formulaRule.hourlyRateCentsPerMaidHour
  );

  return {
    pricingMode: "instant",
    baseTeamMinutes: roundedBaseTeamMinutes,
    finalTeamMinutes,
    durationMinutes: adjustVisitMinutesForTeamSize(finalTeamMinutes, input.teamSize),
    basePriceCents,
    hourlyRateCentsPerMaidHour: formulaRule.hourlyRateCentsPerMaidHour,
    manualReviewReason: ""
  };
}

function resolveResidentialFormulaQuoteOverride(input: {
  catalog: PricebookCatalog;
  service: PricebookRecord;
  scenario: PricebookRecord | null;
  adminDetails: Record<string, string>;
  selectedOptions: PricebookRecord[];
  optionOneMaidMinutes: number;
  teamSize: number;
}) {
  if (hasRemodelingSelection(input.adminDetails)) {
    return null;
  }
  if (isBuildingCommonAreasScenario(input.scenario, input.service)) {
    return null;
  }

  const bucket = getResidentialFormulaBucket(input.scenario, input.service);
  if (!bucket) {
    return null;
  }

  const advancedRules = input.catalog.settings?.advancedRules ?? {};
  const formulaRule = normalizeResidentialComponentFormulaRules(advancedRules.residentialComponentFormulaRules)
    .find((entry) => entry.bucket === bucket);
  if (!formulaRule) {
    return null;
  }

  const squareFeet = readSquareFootageMax(input.adminDetails.square_footage);
  const bedrooms = readNumberFromDetails(input.adminDetails, ["bedrooms"]);
  const bathroomCounts = readBathroomCounts(input.adminDetails);
  const levels = readNumberFromDetails(input.adminDetails, ["levels"]);

  if (
    squareFeet == null ||
    squareFeet <= 0 ||
    bedrooms == null ||
    bedrooms < 0 ||
    !bathroomCounts ||
    levels == null ||
    levels < 1
  ) {
    return null;
  }

  const normalizedLevels = normalizeHalfLevel(levels);
  const minimumSquareFeetForLevel = getResidentialFormulaMinimumSquareFeetForLevel(
    normalizedLevels,
    normalizeResidentialFormulaLevelSquareFootageMinimums(advancedRules.residentialFormulaLevelSquareFootageMinimums),
    normalizeResidentialFormulaMissingHalfLevelIncrement(advancedRules.residentialFormulaMissingHalfLevelIncrement)
  );
  const billableSquareFeet = Math.max(squareFeet, minimumSquareFeetForLevel);
  const serviceType = deriveResidentialServiceType(input.adminDetails, input.selectedOptions);
  const serviceProfile = normalizeResidentialHourlyServiceProfiles(advancedRules.residentialHourlyServiceProfiles)
    .find((entry) => entry.serviceType === serviceType);
  if (!serviceProfile) {
    return null;
  }

  const conditionAreas = readResidentialConditionAreas(input.adminDetails);
  const conditionMultipliers = normalizeResidentialComponentConditionMultipliers(
    advancedRules.residentialComponentConditionMultipliers
  );
  const conditionMultiplierFor = (area: ResidentialConditionArea) =>
    conditionMultipliers[area][conditionAreas[area]] ?? 1;
  const skipAreaPropertyType = getSkipAreaPropertyType(input.scenario, input.service);
  const skipAreaSelection =
    input.scenario && skipAreaPropertyType
      ? getSkipAreaSelectionData(input.adminDetails, normalizeStringList(input.scenario.allowedSkipAreas))
      : { selectedAreas: [], bathroomsCount: 0, bedroomsCount: 0 };
  const selectedSkipAreas = new Set(skipAreaSelection.selectedAreas);
  const skippedBathroomUnits = selectedSkipAreas.has("bathrooms")
    ? Math.min(
        skipAreaSelection.bathroomsCount,
        bathroomCounts.fullBathrooms + bathroomCounts.halfBathrooms * 0.5
      )
    : 0;
  const skippedFullBathrooms = Math.min(bathroomCounts.fullBathrooms, Math.floor(skippedBathroomUnits));
  const skippedHalfBathrooms = Math.min(
    bathroomCounts.halfBathrooms,
    Math.round(Math.max(0, skippedBathroomUnits - skippedFullBathrooms) * 2)
  );
  const skippedBedrooms = selectedSkipAreas.has("bedrooms")
    ? Math.min(bedrooms, skipAreaSelection.bedroomsCount)
    : 0;
  const skipCommonAreas =
    selectedSkipAreas.has("living_areas") ||
    selectedSkipAreas.has("living_room") ||
    selectedSkipAreas.has("dining_room") ||
    selectedSkipAreas.has("hallways");
  const extraHalfLevels = Math.max(0, Math.round((normalizedLevels - 1) * 2));
  const calculateComponentMinutes = (skipSelectedAreas: boolean) => {
    const kitchenTeamMinutes = skipSelectedAreas && selectedSkipAreas.has("kitchen")
      ? 0
      : formulaRule.kitchenTeamMinutes * conditionMultiplierFor("kitchen");
    const fullBathrooms = skipSelectedAreas
      ? Math.max(0, bathroomCounts.fullBathrooms - skippedFullBathrooms)
      : bathroomCounts.fullBathrooms;
    const halfBathrooms = skipSelectedAreas
      ? Math.max(0, bathroomCounts.halfBathrooms - skippedHalfBathrooms)
      : bathroomCounts.halfBathrooms;
    const bathroomTeamMinutes =
      (fullBathrooms * formulaRule.minutesPerFullBathroom + halfBathrooms * formulaRule.minutesPerHalfBathroom) *
      conditionMultiplierFor("bathrooms");
    const bedroomTeamMinutes =
      Math.max(0, bedrooms - (skipSelectedAreas ? skippedBedrooms : 0)) *
      formulaRule.minutesPerBedroom *
      conditionMultiplierFor("bedrooms");
    const commonAreaTeamMinutes = skipSelectedAreas && skipCommonAreas
      ? 0
      : billableSquareFeet * formulaRule.minutesPerSquareFoot * conditionMultiplierFor("otherAreas");
    const levelTeamMinutes = extraHalfLevels * formulaRule.minutesPerExtraHalfLevel;
    const unconditionedTeamMinutes =
      formulaRule.setupTeamMinutes +
      (skipSelectedAreas && selectedSkipAreas.has("kitchen") ? 0 : formulaRule.kitchenTeamMinutes) +
      (fullBathrooms * formulaRule.minutesPerFullBathroom + halfBathrooms * formulaRule.minutesPerHalfBathroom) +
      Math.max(0, bedrooms - (skipSelectedAreas ? skippedBedrooms : 0)) * formulaRule.minutesPerBedroom +
      (skipSelectedAreas && skipCommonAreas ? 0 : billableSquareFeet * formulaRule.minutesPerSquareFoot) +
      levelTeamMinutes;
    const conditionedTeamMinutes =
      formulaRule.setupTeamMinutes +
      kitchenTeamMinutes +
      bathroomTeamMinutes +
      bedroomTeamMinutes +
      commonAreaTeamMinutes +
      levelTeamMinutes;

    return {
      setupTeamMinutes: formulaRule.setupTeamMinutes,
      kitchenTeamMinutes,
      bathroomTeamMinutes,
      bedroomTeamMinutes,
      commonAreaTeamMinutes,
      levelTeamMinutes,
      unconditionedTeamMinutes,
      conditionedTeamMinutes,
      rawTeamMinutes: conditionedTeamMinutes * serviceProfile.serviceTimeMultiplier
    };
  };
  const unskippedComponents = calculateComponentMinutes(false);
  const skippedComponents = calculateComponentMinutes(true);
  const unskippedRoundedBaseTeamMinutes = Math.max(
    serviceProfile.minimumTeamMinutes,
    roundUpToNearest15(unskippedComponents.rawTeamMinutes)
  );
  const roundedBaseTeamMinutes = Math.max(
    serviceProfile.minimumTeamMinutes,
    roundUpToNearest15(skippedComponents.rawTeamMinutes)
  );
  const unskippedFinalTeamMinutes = roundUpToNearest15(
    unskippedRoundedBaseTeamMinutes + Math.max(0, input.optionOneMaidMinutes) / 2
  );
  const finalTeamMinutes = roundUpToNearest15(roundedBaseTeamMinutes + Math.max(0, input.optionOneMaidMinutes) / 2);
  const unskippedBasePriceCents = Math.round(
    (unskippedRoundedBaseTeamMinutes / 60) *
      apartmentFlatFeeDefaultCrewSize *
      serviceProfile.hourlyRateCentsPerMaidHour
  );
  const basePriceCents = Math.round(
    (roundedBaseTeamMinutes / 60) *
      apartmentFlatFeeDefaultCrewSize *
      serviceProfile.hourlyRateCentsPerMaidHour
  );
  const durationMinutes = adjustVisitMinutesForTeamSize(finalTeamMinutes, input.teamSize);
  const skipAreaDeductionAmount = Math.max(0, unskippedBasePriceCents - basePriceCents);
  const skipAreaDurationDeductionMinutes = Math.max(
    0,
    Math.round(adjustVisitMinutesForTeamSize(unskippedFinalTeamMinutes, input.teamSize) - durationMinutes)
  );
  const actualSkipLaborMinutes = skipAreaDurationDeductionMinutes * Math.max(1, input.teamSize);
  const removedComponents = [
    {
      code: "kitchen" as const,
      label: "Kitchen",
      quantityLabel: "Skip",
      removedTeamMinutes: Math.max(0, unskippedComponents.kitchenTeamMinutes - skippedComponents.kitchenTeamMinutes)
    },
    {
      code: "bathrooms" as const,
      label: "Bathrooms",
      quantityLabel: formatHalfStepValue(skippedFullBathrooms + skippedHalfBathrooms * 0.5),
      removedTeamMinutes: Math.max(0, unskippedComponents.bathroomTeamMinutes - skippedComponents.bathroomTeamMinutes)
    },
    {
      code: "bedrooms" as const,
      label: "Bedrooms / offices / dens",
      quantityLabel: String(skippedBedrooms),
      removedTeamMinutes: Math.max(0, unskippedComponents.bedroomTeamMinutes - skippedComponents.bedroomTeamMinutes)
    },
    {
      code: "living_areas" as const,
      label: "Living areas",
      quantityLabel: "Skip",
      removedTeamMinutes: Math.max(0, unskippedComponents.commonAreaTeamMinutes - skippedComponents.commonAreaTeamMinutes)
    }
  ].filter((entry) => entry.removedTeamMinutes > 0);
  const totalRemovedTeamMinutes = removedComponents.reduce((total, entry) => total + entry.removedTeamMinutes, 0);
  let allocatedLaborMinutes = 0;
  let allocatedAmountCents = 0;
  const skipAreaDeductionRows: SkipAreaDeductionRow[] =
    totalRemovedTeamMinutes > 0 && (actualSkipLaborMinutes > 0 || skipAreaDeductionAmount > 0)
      ? removedComponents.map((entry, index) => {
          const isLast = index === removedComponents.length - 1;
          const oneMaidMinutes = isLast
            ? Math.max(0, actualSkipLaborMinutes - allocatedLaborMinutes)
            : Math.round((actualSkipLaborMinutes * (entry.removedTeamMinutes / totalRemovedTeamMinutes)) * 10) / 10;
          const amountCents = isLast
            ? Math.max(0, skipAreaDeductionAmount - allocatedAmountCents)
            : Math.round(skipAreaDeductionAmount * (entry.removedTeamMinutes / totalRemovedTeamMinutes));
          allocatedLaborMinutes += oneMaidMinutes;
          allocatedAmountCents += amountCents;
          return {
            code: entry.code,
            label: entry.label,
            quantityLabel: entry.quantityLabel,
            oneMaidMinutes,
            twoMaidMinutes: oneMaidMinutes / 2,
            amountCents
          };
        })
      : [];
  const conditionMultiplier = skippedComponents.unconditionedTeamMinutes > 0
    ? skippedComponents.conditionedTeamMinutes / skippedComponents.unconditionedTeamMinutes
    : 1;

  return {
    bucket,
    serviceType,
    serviceLabel: serviceProfile.label,
    baseTeamMinutes: roundedBaseTeamMinutes,
    finalTeamMinutes,
    durationMinutes,
    basePriceCents,
    hourlyRateCentsPerMaidHour: serviceProfile.hourlyRateCentsPerMaidHour,
    conditionMultiplier,
    skipAreaDeductionAmount,
    skipAreaDurationDeductionMinutes,
    skipAreaDeductionRows
  } satisfies ResidentialFormulaQuoteOverride;
}

function resolveServiceScenario(
  scenarios: PricebookServiceScenario[],
  serviceId: string,
  squareFootageValue: string
) {
  const selectedSquareFootage = squareFootageIndex(squareFootageValue);
  const candidates = scenarios.filter((scenario) => {
    if (String(scenario.baseServiceId ?? scenario.base_service_id ?? "") !== serviceId) {
      return false;
    }

    const min = scenario.squareFootageMin == null ? null : Number(scenario.squareFootageMin);
    const max = scenario.squareFootageMax == null ? null : Number(scenario.squareFootageMax);
    if (min == null && max == null) {
      return true;
    }
    if (selectedSquareFootage == null) {
      return false;
    }
    if (min != null && selectedSquareFootage < min) {
      return false;
    }
    if (max != null && selectedSquareFootage > max) {
      return false;
    }
    return true;
  });

  if (!candidates.length) {
    return null;
  }

  return candidates.sort((left, right) => {
    const priorityDelta = Number(right.priority ?? 0) - Number(left.priority ?? 0);
    if (priorityDelta !== 0) return priorityDelta;
    const orderDelta = Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
    if (orderDelta !== 0) return orderDelta;
    return String(left.name ?? "").localeCompare(String(right.name ?? ""));
  })[0];
}

function normalizeRemodelingOptionCode(value: unknown) {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (normalized === "bathroom_remodeling") return "bathrooms";
  if (normalized === "living_areas_remodeling") return "living_areas";
  if (normalized === "floor_refinishing") return "floors_refinishing";
  if (normalized === "wall_painting") return "walls_painting";
  if (normalized === "kitchen_remodeling") return "kitchen";
  return normalized;
}

function parseRemodelingPricingRules(value: unknown) {
  if (!Array.isArray(value)) {
    return [] as RemodelingPricingRule[];
  }

  return value
    .map((entry) => {
      if (!isRecord(entry)) return null;
      const optionCode = normalizeRemodelingOptionCode(entry.optionCode);
      if (!optionCode) return null;
      const hourlyRatePerMaidHour = Number(entry.hourlyRatePerMaidHour ?? 0);
      const minimumMaidCount = Number(entry.minimumMaidCount ?? 0);
      const minimumHours = Number(entry.minimumHours ?? 0);
      return {
        optionCode,
        optionLabel: String(entry.optionLabel ?? remodelingOptionLabelsByCode[optionCode] ?? optionCode),
        hourlyRatePerMaidHour: Number.isFinite(hourlyRatePerMaidHour) ? hourlyRatePerMaidHour : 0,
        minimumMaidCount: Number.isFinite(minimumMaidCount) ? minimumMaidCount : 0,
        minimumHours: Number.isFinite(minimumHours) ? minimumHours : 0,
        active: normalizeActive(entry.active)
      };
    })
    .filter((rule): rule is RemodelingPricingRule => Boolean(rule));
}

function getSelectedPartialRemodelingAreaCodes(adminDetails: Record<string, string>) {
  return normalizeStringList(adminDetails.partial_remodeling_areas).map(normalizeRemodelingOptionCode).filter(Boolean);
}

function getSelectedRemodelingPricingCodes(adminDetails: Record<string, string>) {
  const remodelingTypeCode = normalizeRemodelingOptionCode(adminDetails.remodeling_type);
  if (!remodelingTypeCode) {
    return [] as string[];
  }
  if (remodelingTypeCode === "partial_remodeling") {
    const partialAreaCodes = getSelectedPartialRemodelingAreaCodes(adminDetails);
    return partialAreaCodes.length ? partialAreaCodes : ["partial_remodeling"];
  }
  return [remodelingTypeCode];
}

function normalizeHalfStepValue(value: number) {
  return Math.max(0, Math.round(value * 2) / 2);
}

function formatHalfStepValue(value: number) {
  const normalized = normalizeHalfStepValue(value);
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(1);
}

function formatPartialRemodelAreaLabel(optionCode: string, baseLabel: string, adminDetails: Record<string, string>) {
  if (optionCode === "bedrooms") {
    const count = String(adminDetails.partial_remodeling_bedrooms_count ?? "").trim();
    return count ? `${baseLabel} (${count})` : baseLabel;
  }
  if (optionCode === "bathrooms") {
    const count = String(adminDetails.partial_remodeling_bathrooms_count ?? "").trim();
    return count ? `${baseLabel} (${count})` : baseLabel;
  }
  return baseLabel;
}

function getRemodelingOptionLabel(optionCode: string, selectedCodes: string[] = [], adminDetails: Record<string, string> = {}) {
  if (optionCode === "partial_remodeling" && selectedCodes.length) {
    const labels = selectedCodes.map((code) =>
      formatPartialRemodelAreaLabel(code, remodelingOptionLabelsByCode[code] ?? code, adminDetails)
    );
    return `Partial renovation (${labels.join(", ")})`;
  }
  return remodelingOptionLabelsByCode[optionCode] ?? optionCode;
}

function resolveRemodelingHourlyQuote(scenario: PricebookRecord | null, adminDetails: Record<string, string>) {
  if (!scenario || String(adminDetails.recent_remodeling ?? "no").trim().toLowerCase() !== "yes") {
    return null;
  }
  const remodelingTypeCode = normalizeRemodelingOptionCode(adminDetails.remodeling_type);
  if (!remodelingTypeCode) {
    return null;
  }
  const selectedPricingCodes = getSelectedRemodelingPricingCodes(adminDetails);
  if (!selectedPricingCodes.length) {
    return null;
  }
  const rules = parseRemodelingPricingRules(scenario.remodelingPricingRules ?? scenario.remodeling_pricing_rules);
  if (!rules.length) {
    return null;
  }
  const activeRules = rules.filter((rule) => rule.active);
  const matchingRules = activeRules.filter((rule) => selectedPricingCodes.includes(rule.optionCode)) || [];
  const fallbackRules = remodelingTypeCode === "partial_remodeling"
    ? activeRules.filter((rule) => rule.optionCode === "partial_remodeling")
    : [];
  const resolvedRules = matchingRules.length ? matchingRules : fallbackRules;
  if (!resolvedRules.length) {
    return null;
  }

  const hourlyRatePerMaidHour = Math.max(...resolvedRules.map((rule) => Math.max(0, rule.hourlyRatePerMaidHour)));
  const minimumMaidCount = Math.max(...resolvedRules.map((rule) => Math.max(0, rule.minimumMaidCount)));
  const minimumHours = resolvedRules.reduce((total, rule) => total + Math.max(0, rule.minimumHours), 0);
  if (hourlyRatePerMaidHour <= 0 || minimumMaidCount <= 0 || minimumHours <= 0) {
    return null;
  }

  const minimumLaborHours = minimumMaidCount * minimumHours;
  const startingAtAmount = Math.round(hourlyRatePerMaidHour * minimumLaborHours * 100) / 100;
  return {
    remodelingTypeCode,
    selectedPricingCodes,
    optionLabel: getRemodelingOptionLabel(remodelingTypeCode, selectedPricingCodes, adminDetails),
    hourlyRatePerMaidHour,
    minimumMaidCount,
    minimumHours,
    minimumLaborHours,
    startingAtAmount
  };
}

function hasRemodelingSelection(adminDetails: Record<string, string>) {
  return String(adminDetails.recent_remodeling ?? "no").trim().toLowerCase() === "yes" &&
    Boolean(normalizeRemodelingOptionCode(adminDetails.remodeling_type));
}

function resolveTipAmount(adminDetails: Record<string, string>, serviceTotalBeforeTip: number) {
  const tipType = String(adminDetails.tip_type ?? "").trim().toLowerCase();
  const rawTip = String(adminDetails.tip ?? "").trim();
  const tipValue = Number(rawTip);
  if (!tipType || !rawTip || !Number.isFinite(tipValue) || tipValue <= 0) {
    return 0;
  }
  if (tipType === "percent") {
    return Math.round(serviceTotalBeforeTip * (Math.min(100, Math.max(0, tipValue)) / 100));
  }
  if (tipType === "flat") {
    return Math.round(Math.min(500, Math.max(0, tipValue)) * 100);
  }
  return 0;
}

function normalizeWholeNumber(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function getCarpetShampooingOptionResolution(option: PricebookRecord, adminDetails: Record<string, string>) {
  const rawConfig = option.carpetShampooingConfig;
  const config = isRecord(rawConfig) ? rawConfig : null;
  if (!config?.enabled) {
    return null;
  }

  const units = Array.isArray(config.units) ? config.units.filter(isRecord) : [];
  let totalQuantity = 0;
  let priceDeltaAmount = 0;
  let durationDeltaMinutes = 0;

  for (const unit of units) {
    const code = String(unit.code ?? "") as CarpetUnitCode;
    if (!(code in carpetUnitAdminDetailKeys) || !normalizeActive(unit.active)) {
      continue;
    }
    const quantity = normalizeWholeNumber(adminDetails[carpetUnitAdminDetailKeys[code]]);
    totalQuantity += quantity;
    priceDeltaAmount += quantity * Number(unit.priceDeltaAmount ?? 0);
    durationDeltaMinutes += quantity * Number(unit.durationDeltaMinutes ?? 0);
  }

  if (totalQuantity > 0) {
    priceDeltaAmount = Math.max(priceDeltaAmount, Number(config.minimumOrderAmount ?? 0));
  }
  return { totalQuantity, priceDeltaAmount, durationDeltaMinutes };
}

function isDishesHandWashingOption(option: PricebookRecord) {
  const token = normalizeIdentifier(
    [option.name, option.estimatorExtraCode, option.estimator_extra_code, option.code].filter(Boolean).join(" ")
  );
  return token.includes("dishes_hand_washing") || (token.includes("dishes") && token.includes("hand"));
}

function isLaundryServiceOption(option: PricebookRecord) {
  const token = normalizeIdentifier(
    [option.name, option.estimatorExtraCode, option.estimator_extra_code, option.code].filter(Boolean).join(" ")
  );
  return token.includes("laundry_service") || token.includes("laundry");
}

function isSimpleQuantityOption(option: PricebookRecord) {
  const rawConfig = option.carpetShampooingConfig;
  const carpetConfig = isRecord(rawConfig) ? rawConfig : null;
  return !carpetConfig?.enabled &&
    (Boolean(option.quantityBased ?? option.quantity_based) || isDishesHandWashingOption(option) || isLaundryServiceOption(option));
}

function getSimpleQuantityAdminDetailKey(option: PricebookRecord) {
  const configuredKey = String(option.quantityAdminDetailKey ?? option.quantity_admin_detail_key ?? "").trim();
  if (configuredKey) {
    return configuredKey;
  }
  if (isDishesHandWashingOption(option)) {
    return "dishes_hand_washing_sinkfuls";
  }
  if (isLaundryServiceOption(option)) {
    return "laundry_service_loads";
  }
  return `extra_quantity_${normalizeIdentifier(option.id)}`;
}

function normalizeSimpleQuantity(value: unknown, option: PricebookRecord) {
  const minQuantity = Math.max(1, Math.round(Number(option.minQuantity ?? option.min_quantity ?? 1) || 1));
  const configuredMax = Math.round(Number(option.maxQuantity ?? option.max_quantity ?? 0) || 0);
  const maxQuantity = Math.max(minQuantity, configuredMax > 0 ? configuredMax : 10);
  const parsed = Math.round(Number(value ?? minQuantity));
  if (!Number.isFinite(parsed)) {
    return minQuantity;
  }
  return Math.min(maxQuantity, Math.max(minQuantity, parsed));
}

function getSimpleQuantityOptionResolution(
  option: PricebookRecord,
  adminDetails: Record<string, string>,
  unitPriceDeltaAmount: number,
  unitDurationDeltaMinutes: number
) {
  if (!isSimpleQuantityOption(option)) {
    return null;
  }

  const quantity = normalizeSimpleQuantity(adminDetails[getSimpleQuantityAdminDetailKey(option)], option);
  const fallbackPriceDeltaAmount =
    isDishesHandWashingOption(option) && unitPriceDeltaAmount <= 0
      ? 1200
      : isLaundryServiceOption(option) && unitPriceDeltaAmount <= 0
        ? 2600
        : unitPriceDeltaAmount;
  const fallbackDurationDeltaMinutes =
    isDishesHandWashingOption(option) && unitDurationDeltaMinutes <= 0
      ? 15
      : isLaundryServiceOption(option) && unitDurationDeltaMinutes <= 0
        ? 30
        : unitDurationDeltaMinutes;

  return {
    quantity,
    quantityLabel: isDishesHandWashingOption(option) ? "sinkful" : isLaundryServiceOption(option) ? "load" : "item",
    priceDeltaAmount: fallbackPriceDeltaAmount * quantity,
    durationDeltaMinutes: fallbackDurationDeltaMinutes * quantity
  };
}

function parseNumericToken(value: unknown) {
  const match = String(value ?? "").match(/-?\d+(?:\.\d+)?/);
  if (!match) {
    return null;
  }
  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : null;
}

function squareFootageIndex(value: unknown) {
  const range = parseSquareFootageRange(String(value ?? ""));
  if (range) {
    return range.max;
  }
  return parseNumericToken(value);
}

function matchesRange(value: number | null, minValue: number | null, maxValue: number | null) {
  if (minValue == null && maxValue == null) return true;
  if (value == null) return false;
  if (minValue != null && value < minValue) return false;
  if (maxValue != null && value > maxValue) return false;
  return true;
}

function ruleSpecificity(rule: PricebookRecord) {
  let score = 0;
  if (normalizeStringList(rule.serviceIds).length) score += 8;
  if (String(rule.squareFootageFrom ?? "").trim() || String(rule.squareFootageTo ?? "").trim()) score += 4;
  if (String(rule.bedroomsFrom ?? "").trim() || String(rule.bedroomsTo ?? "").trim()) score += 2;
  if (String(rule.levelsFrom ?? "").trim() || String(rule.levelsTo ?? "").trim()) score += 1;
  return score;
}

function resolveExtraRule(option: PricebookRecord, serviceId: string, adminDetails: Record<string, string>) {
  const rules = Array.isArray(option.extraPricingRules) ? option.extraPricingRules.filter(isRecord) : [];
  if (!rules.length) {
    return null;
  }

  const selectedBedrooms = parseNumericToken(adminDetails.bedrooms);
  const selectedLevels = parseNumericToken(adminDetails.levels);
  const selectedSquareFootageIndex = squareFootageIndex(adminDetails.square_footage);
  const matchingRules = rules.filter((rule) => {
    if (!normalizeActive(rule.active)) return false;
    const serviceIds = normalizeStringList(rule.serviceIds);
    if (serviceIds.length && !serviceIds.includes(serviceId)) return false;
    if (!matchesRange(selectedSquareFootageIndex, squareFootageIndex(rule.squareFootageFrom), squareFootageIndex(rule.squareFootageTo))) return false;
    if (!matchesRange(selectedBedrooms, parseNumericToken(rule.bedroomsFrom), parseNumericToken(rule.bedroomsTo))) return false;
    if (!matchesRange(selectedLevels, parseNumericToken(rule.levelsFrom), parseNumericToken(rule.levelsTo))) return false;
    return true;
  });

  if (!matchingRules.length) {
    return null;
  }

  return matchingRules.sort((left, right) => {
    const specificityDifference = ruleSpecificity(right) - ruleSpecificity(left);
    if (specificityDifference !== 0) return specificityDifference;
    return Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0);
  })[0];
}

function toValidSkipAreaCode(value: string): SkipAreaCode | null {
  switch (value) {
    case "kitchen":
    case "bathrooms":
    case "bedrooms":
    case "living_areas":
    case "living_room":
    case "dining_room":
    case "hallways":
      return value;
    default:
      return null;
  }
}

function getSkipAreaPropertyType(
  scenario: PricebookRecord | null,
  service: PricebookRecord
): "apartment" | "condo" | "townhouse" | "single_family" | null {
  const scenarioCode = String(scenario?.code ?? "").toLowerCase();
  const serviceName = String(service.name ?? "").toLowerCase();
  if (scenarioCode.startsWith("apartment_") || serviceName.includes("apartment") || serviceName.includes("condo")) {
    return "apartment";
  }
  if (scenarioCode === "townhouse" || serviceName.includes("townhouse")) return "townhouse";
  if (scenarioCode === "single_family" || serviceName.includes("single-family")) return "single_family";
  return null;
}

function getSkipAreaSelectionData(adminDetails: Record<string, string>, allowedSkipAreas: string[]) {
  const allowed = new Set(allowedSkipAreas);
  const selectedAreas = normalizeStringList(adminDetails.skip_areas)
    .map((value) => toValidSkipAreaCode(String(value)))
    .filter((value): value is SkipAreaCode => value !== null && allowed.has(value));
  return {
    selectedAreas,
    bathroomsCount: normalizeHalfStepValue(Number(adminDetails.skip_bathrooms_count ?? 0) || 0),
    bedroomsCount: Math.max(0, Math.round(Number(adminDetails.skip_bedrooms_count ?? 0) || 0))
  };
}

function getResidentialSkipAreaPropertyGroup(
  propertyType: "apartment" | "condo" | "townhouse" | "single_family"
): ResidentialSkipAreaPropertyGroup {
  return propertyType === "apartment" || propertyType === "condo" ? "apartment_condo" : "house";
}

function skipAreaDurationRuleMatchesSquareFootage(rule: ResidentialSkipAreaDurationRule, squareFootageValue: string) {
  const range = parseSquareFootageRange(squareFootageValue);
  if (!range) {
    return true;
  }

  if (rule.squareFootageMin != null && range.max < rule.squareFootageMin) {
    return false;
  }

  if (rule.squareFootageMax != null && range.max > rule.squareFootageMax) {
    return false;
  }

  return true;
}

function getResidentialSkipAreaLaborMinutes(input: {
  rules: ResidentialSkipAreaDurationRule[];
  propertyType: "apartment" | "condo" | "townhouse" | "single_family";
  areaCode: ResidentialSkipAreaDurationAreaCode;
  squareFootageValue: string;
}) {
  const propertyGroup = getResidentialSkipAreaPropertyGroup(input.propertyType);
  const matchingRule = input.rules.find(
    (rule) =>
      rule.propertyGroup === propertyGroup &&
      rule.areaCode === input.areaCode &&
      skipAreaDurationRuleMatchesSquareFootage(rule, input.squareFootageValue)
  );
  return Math.max(0, Number(matchingRule?.laborMinutes ?? 0));
}

function calculateSkipAreaDeductionCents(oneMaidMinutes: number, hourlyRateCentsPerMaidHour: number) {
  return oneMaidMinutes <= 0 || hourlyRateCentsPerMaidHour <= 0
    ? 0
    : Math.round((oneMaidMinutes / 60) * hourlyRateCentsPerMaidHour);
}

function calculateHourlyRateCentsPerMaidHourFromVisit(
  amountCents: number,
  visitMinutes: number,
  teamSize: number
) {
  const laborHours = (Math.max(0, visitMinutes) * Math.max(1, teamSize)) / 60;
  return laborHours > 0 && amountCents > 0 ? Math.round(amountCents / laborHours) : 0;
}

function buildSkipAreaDeductionRow(
  code: SkipAreaCode,
  label: string,
  quantityLabel: string,
  oneMaidMinutes: number,
  hourlyRateCentsPerMaidHour: number
): SkipAreaDeductionRow {
  return {
    code,
    label,
    quantityLabel,
    oneMaidMinutes,
    twoMaidMinutes: oneMaidMinutes / 2,
    amountCents: calculateSkipAreaDeductionCents(oneMaidMinutes, hourlyRateCentsPerMaidHour)
  };
}

function getSkipAreaDeductionRows(
  propertyType: "apartment" | "condo" | "townhouse" | "single_family",
  scenario: PricebookRecord,
  adminDetails: Record<string, string>,
  hourlyRateCentsPerMaidHour: number,
  durationRules: ResidentialSkipAreaDurationRule[]
) {
  const selection = getSkipAreaSelectionData(adminDetails, normalizeStringList(scenario.allowedSkipAreas));
  const rows: SkipAreaDeductionRow[] = [];
  const squareFootageValue = String(adminDetails.square_footage ?? "");
  const laborMinutesFor = (areaCode: ResidentialSkipAreaDurationAreaCode) =>
    getResidentialSkipAreaLaborMinutes({
      rules: durationRules,
      propertyType,
      areaCode,
      squareFootageValue
    });

  if (selection.selectedAreas.includes("kitchen")) {
    rows.push(buildSkipAreaDeductionRow("kitchen", "Kitchen", "Skip", laborMinutesFor("kitchen"), hourlyRateCentsPerMaidHour));
  }
  if (selection.selectedAreas.includes("bathrooms") && selection.bathroomsCount > 0) {
    const fullBathrooms = Math.floor(selection.bathroomsCount);
    const halfBathrooms = Math.round((selection.bathroomsCount - fullBathrooms) * 2);
    rows.push(
      buildSkipAreaDeductionRow(
        "bathrooms",
        "Bathrooms",
        formatHalfStepValue(selection.bathroomsCount),
        fullBathrooms * laborMinutesFor("full_bathroom") + halfBathrooms * laborMinutesFor("half_bathroom"),
        hourlyRateCentsPerMaidHour
      )
    );
  }
  if (selection.selectedAreas.includes("bedrooms") && selection.bedroomsCount > 0) {
    rows.push(buildSkipAreaDeductionRow("bedrooms", "Bedrooms / offices / dens", String(selection.bedroomsCount), selection.bedroomsCount * laborMinutesFor("bedroom"), hourlyRateCentsPerMaidHour));
  }
  if (selection.selectedAreas.includes("living_areas")) {
    rows.push(buildSkipAreaDeductionRow("living_areas", "Living areas", "Skip", laborMinutesFor("living_areas"), hourlyRateCentsPerMaidHour));
  }
  if (selection.selectedAreas.includes("living_room")) {
    rows.push(buildSkipAreaDeductionRow("living_room", "Living room", "Skip", laborMinutesFor("living_room"), hourlyRateCentsPerMaidHour));
  }
  if (selection.selectedAreas.includes("dining_room")) {
    rows.push(buildSkipAreaDeductionRow("dining_room", "Dining room", "Skip", laborMinutesFor("dining_room"), hourlyRateCentsPerMaidHour));
  }
  if (selection.selectedAreas.includes("hallways")) {
    rows.push(buildSkipAreaDeductionRow("hallways", "Hallways", "Skip", laborMinutesFor("hallways"), hourlyRateCentsPerMaidHour));
  }

  return rows;
}

function isPromotionValidForServiceDate(promotion: PricebookRecord, serviceDate?: string) {
  if (!serviceDate) {
    return true;
  }
  const serviceDateStart = String(promotion.serviceDateStart ?? "").trim();
  if (serviceDateStart && serviceDate < serviceDateStart) {
    return false;
  }
  const serviceDateEnd = String(promotion.serviceDateEnd ?? "").trim();
  if (serviceDateEnd && serviceDate > serviceDateEnd) {
    return false;
  }
  const validWeekdays = normalizeStringList(promotion.validWeekdays).map((entry) => entry.toLowerCase());
  return !validWeekdays.length || validWeekdays.includes(weekdayForDate(serviceDate, "America/Chicago"));
}

function getPromotionCurrentStatus(promotion: PricebookRecord, nowInput?: number | Date) {
  const now = nowInput instanceof Date ? nowInput.getTime() : Number(nowInput ?? Date.now());
  const startsAt = String(promotion.startsAt ?? promotion.starts_at ?? "").trim();
  if (startsAt) {
    const startsAtMs = Date.parse(startsAt);
    if (Number.isFinite(startsAtMs) && startsAtMs > now) {
      return "not_started" as const;
    }
  }
  const endsAt = String(promotion.endsAt ?? promotion.ends_at ?? "").trim();
  if (endsAt) {
    const endsAtMs = Date.parse(endsAt);
    if (Number.isFinite(endsAtMs) && endsAtMs < now) {
      return "expired" as const;
    }
  }
  return "active" as const;
}

function normalizeArrivalFlexibilityTime(value: unknown) {
  const normalized = String(value ?? "").trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(normalized);
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return "";
  }
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function formatClockLabel(value: string) {
  const normalized = normalizeArrivalFlexibilityTime(value) || String(value ?? "").trim();
  const [rawHours, rawMinutes] = normalized.split(":");
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return { timeText: normalized, meridiem: "" };
  }
  const meridiem = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const minuteText = String(minutes).padStart(2, "0");
  return { timeText: minuteText === "00" ? String(displayHours) : `${displayHours}:${minuteText}`, meridiem };
}

function formatArrivalWindowLabel(startTime: string, endTime: string) {
  const start = formatClockLabel(startTime);
  const end = formatClockLabel(endTime);
  if (start.meridiem && start.meridiem === end.meridiem) {
    return `${start.timeText}-${end.timeText} ${end.meridiem}`;
  }
  return `${start.timeText}${start.meridiem ? ` ${start.meridiem}` : ""}-${end.timeText}${end.meridiem ? ` ${end.meridiem}` : ""}`;
}

function normalizeArrivalFlexibilitySettings(value: ArrivalFlexibilityQuoteSettings | null | undefined) {
  const source = isRecord(value) ? value : {};
  const excludedWindowStartTimes = Array.isArray(source.excludedWindowStartTimes)
    ? source.excludedWindowStartTimes.map(normalizeArrivalFlexibilityTime).filter(Boolean)
    : defaultArrivalFlexibilitySettings.excludedWindowStartTimes;

  return {
    enabled: Boolean(source.enabled ?? defaultArrivalFlexibilitySettings.enabled),
    startTime: normalizeArrivalFlexibilityTime(source.startTime) || defaultArrivalFlexibilitySettings.startTime,
    endTime: normalizeArrivalFlexibilityTime(source.endTime) || defaultArrivalFlexibilitySettings.endTime,
    discountType: source.discountType === "percentage" ? "percentage" as const : "fixed" as const,
    discountValue: Math.max(0, Number(source.discountValue ?? 0) || 0),
    excludedWindowStartTimes: Array.from(new Set(excludedWindowStartTimes))
  };
}

function resolveArrivalFlexibilityOptionState(input: {
  settings?: ArrivalFlexibilityQuoteSettings | null;
  localStartTime?: unknown;
  selectedValue?: unknown;
}) {
  const settings = normalizeArrivalFlexibilitySettings(input.settings);
  const localStartTime = normalizeArrivalFlexibilityTime(input.localStartTime);
  const label = formatArrivalWindowLabel(settings.startTime, settings.endTime);
  const eligible = settings.enabled && Boolean(localStartTime) && !settings.excludedWindowStartTimes.includes(localStartTime);
  return {
    settings,
    localStartTime,
    label,
    eligible,
    selected: eligible && Boolean(String(input.selectedValue ?? "").trim())
  };
}

function applyArrivalFlexibilityDiscount(input: {
  amountCents: number;
  discountType: "fixed" | "percentage";
  discountValue: number;
}) {
  const amountCents = normalizeCents(input.amountCents);
  const discountValue = Math.max(0, Number(input.discountValue ?? 0) || 0);
  if (amountCents <= 0 || discountValue <= 0) {
    return 0;
  }
  return input.discountType === "fixed"
    ? Math.min(amountCents, Math.round(discountValue * 100))
    : Math.min(amountCents, Math.round(amountCents * (discountValue / 100)));
}

function resolveFrequencyRule(catalog: PricebookCatalog, frequencyRuleId: string | undefined, service: PricebookRecord) {
  if (!frequencyRuleId) {
    return null;
  }
  const rule = catalog.frequencyRules.find((entry) => String(entry.id) === frequencyRuleId && normalizeActive(entry.active));
  if (!rule) {
    return null;
  }
  const applicableServiceIds = normalizeStringList(rule.applicableServiceIds ?? rule.applicable_service_ids_json ?? rule.applicableServiceIdsJson);
  const allowedFrequencyRuleIds = normalizeStringList(service.allowedFrequencyRuleIds);
  if (allowedFrequencyRuleIds.length && !allowedFrequencyRuleIds.includes(String(rule.id))) {
    return null;
  }
  if (applicableServiceIds.length && !applicableServiceIds.includes(String(service.id))) {
    return null;
  }
  return rule;
}

function findPromotion(catalog: PricebookCatalog, promoCode?: string) {
  const code = String(promoCode ?? "").trim().toUpperCase();
  if (!code) {
    return null;
  }
  return catalog.promotions.find((promotion) => String(promotion.code ?? "").trim().toUpperCase() === code) ?? null;
}

export function quoteFromPricebook(catalog: PricebookCatalog, input: PricebookQuoteInput): PricebookQuoteResult {
  const service = catalog.services.find((entry) => String(entry.id) === input.serviceId);
  if (!service) {
    throw new Error("Service not found");
  }
  const optionIds = new Set(input.optionIds ?? []);
  const adminDetails = input.adminDetails ?? {};
  const teamSize = normalizeQuoteTeamSize(input.teamSize);

  let resolvedBaseAmount = Number(service.basePriceAmount ?? service.base_price_amount ?? 0);
  let resolvedDurationMinutes = Number(service.durationMinutes ?? service.duration_minutes ?? 0);
  const resolvedScenario = resolveServiceScenario(
    catalog.serviceScenarios.filter((scenario) => normalizeActive(scenario.active)),
    input.serviceId,
    String(adminDetails.square_footage ?? "")
  );
  const selectedOptions = catalog.serviceOptions.filter((option) => optionIds.has(String(option.id)) && normalizeActive(option.active));
  const resolvedOptions: Array<
    PricebookServiceOption & {
      resolvedPriceDeltaAmount: number;
      resolvedDurationDeltaMinutes: number;
      resolvedQuantity: number;
      resolvedQuantityLabel: string;
    }
  > =
    selectedOptions.map((option) => {
      const matchedRule = resolveExtraRule(option, input.serviceId, adminDetails);
      const carpetResolution = getCarpetShampooingOptionResolution(option, adminDetails);
      const unitPriceDeltaAmount = Number(
        matchedRule?.priceDeltaAmount ??
          option.price_delta_amount ??
          option.priceDeltaAmount ??
          0
      );
      const unitDurationDeltaMinutes = Number(
        matchedRule?.durationDeltaMinutes ??
          option.duration_delta_minutes ??
          option.durationDeltaMinutes ??
          0
      );
      const simpleQuantityResolution = getSimpleQuantityOptionResolution(
        option,
        adminDetails,
        unitPriceDeltaAmount,
        unitDurationDeltaMinutes
      );
      return {
        ...option,
        resolvedPriceDeltaAmount: Number(
          carpetResolution?.priceDeltaAmount ??
            simpleQuantityResolution?.priceDeltaAmount ??
            unitPriceDeltaAmount
        ),
        resolvedDurationDeltaMinutes: Number(
          carpetResolution?.durationDeltaMinutes ??
            simpleQuantityResolution?.durationDeltaMinutes ??
            unitDurationDeltaMinutes
        ),
        resolvedQuantity: simpleQuantityResolution?.quantity ?? carpetResolution?.totalQuantity ?? 1,
        resolvedQuantityLabel: simpleQuantityResolution?.quantityLabel ?? ""
      };
    });
  const resolvedOptionOneMaidMinutes = resolvedOptions.reduce(
    (total, option) => total + Math.max(0, Number(option.resolvedDurationDeltaMinutes ?? 0)),
    0
  );
  const pricebookAdvancedRules = catalog.settings?.advancedRules ?? {};

  const apartmentFlatFeeQuoteOverride = resolveApartmentFlatFeeQuoteOverride(service, resolvedScenario, adminDetails);
  if (apartmentFlatFeeQuoteOverride) {
    // Flat-fee apartment packages should stay fixed unless a separate add-on, such as
    // Heavy Condition, is selected. Condition sliders still affect formula-based quotes.
    const conditionMultiplier = 1;
    const adjustedFlatFeeVisitMinutes = roundUpToNearest15(apartmentFlatFeeQuoteOverride.visitMinutes * conditionMultiplier);
    const flatFeeHourlyRateCentsPerMaidHour = calculateHourlyRateCentsPerMaidHourFromVisit(
      apartmentFlatFeeQuoteOverride.basePriceCents,
      apartmentFlatFeeQuoteOverride.visitMinutes,
      apartmentFlatFeeDefaultCrewSize
    );
    resolvedBaseAmount = Math.round(
      (adjustedFlatFeeVisitMinutes / 60) * apartmentFlatFeeDefaultCrewSize * flatFeeHourlyRateCentsPerMaidHour
    );
    resolvedDurationMinutes = adjustVisitMinutesForTeamSize(adjustedFlatFeeVisitMinutes, teamSize);
  }
  const buildingCommonAreasQuoteOverride = resolveBuildingCommonAreasQuoteOverride(service, resolvedScenario, adminDetails);
  const buildingCommonAreasInstantQuoteOverride =
    buildingCommonAreasQuoteOverride?.pricingMode === "instant" ? buildingCommonAreasQuoteOverride : null;
  const buildingCommonAreasManualReviewOnly = buildingCommonAreasQuoteOverride?.pricingMode === "manual";
  if (buildingCommonAreasInstantQuoteOverride) {
    resolvedBaseAmount = buildingCommonAreasInstantQuoteOverride.basePriceCents;
    resolvedDurationMinutes = buildingCommonAreasInstantQuoteOverride.baseMinutes;
  }

  const officeCommercialQuoteOverride =
    !apartmentFlatFeeQuoteOverride && !buildingCommonAreasQuoteOverride
      ? resolveOfficeCommercialQuoteOverride({
          catalog,
          service,
          scenario: resolvedScenario,
          adminDetails,
          optionOneMaidMinutes: resolvedOptionOneMaidMinutes,
          teamSize
        })
      : null;
  const officeCommercialInstantQuoteOverride =
    officeCommercialQuoteOverride?.pricingMode === "instant" ? officeCommercialQuoteOverride : null;
  const officeCommercialManualReviewOnly = officeCommercialQuoteOverride?.pricingMode === "manual";
  if (officeCommercialInstantQuoteOverride) {
    resolvedBaseAmount = officeCommercialInstantQuoteOverride.basePriceCents;
    resolvedDurationMinutes = officeCommercialInstantQuoteOverride.durationMinutes;
  }

  const remodelingHourlyQuote = resolveRemodelingHourlyQuote(resolvedScenario, adminDetails);
  const remodelingReviewOnly = !remodelingHourlyQuote && hasRemodelingSelection(adminDetails);
  const scenarioManualReviewOnly = buildingCommonAreasManualReviewOnly || officeCommercialManualReviewOnly;
  const manualReviewOnly = scenarioManualReviewOnly || remodelingReviewOnly;
  const residentialFormulaQuoteOverride =
    !apartmentFlatFeeQuoteOverride && !buildingCommonAreasQuoteOverride && !officeCommercialQuoteOverride && !remodelingHourlyQuote
      ? resolveResidentialFormulaQuoteOverride({
          catalog,
          service,
          scenario: resolvedScenario,
          adminDetails,
          selectedOptions,
          optionOneMaidMinutes: resolvedOptionOneMaidMinutes,
          teamSize
        })
      : null;
  if (residentialFormulaQuoteOverride) {
    resolvedBaseAmount = residentialFormulaQuoteOverride.basePriceCents;
    resolvedDurationMinutes = residentialFormulaQuoteOverride.durationMinutes;
  }

  const skipAreaPropertyType = getSkipAreaPropertyType(resolvedScenario, service);
  const skipAreaDurationRules = normalizeResidentialSkipAreaDurationRules(
    pricebookAdvancedRules.residentialSkipAreaDurationRules
  );
  const skipAreaHourlyRateCentsPerMaidHour =
    residentialFormulaQuoteOverride?.hourlyRateCentsPerMaidHour ??
    calculateHourlyRateCentsPerMaidHourFromVisit(resolvedBaseAmount, resolvedDurationMinutes, teamSize);
  const skipAreaDeductionRows =
    residentialFormulaQuoteOverride
      ? residentialFormulaQuoteOverride.skipAreaDeductionRows
      : !remodelingHourlyQuote && resolvedScenario && skipAreaPropertyType
      ? getSkipAreaDeductionRows(
          skipAreaPropertyType,
          resolvedScenario,
          adminDetails,
          skipAreaHourlyRateCentsPerMaidHour,
          skipAreaDurationRules
        )
      : [];
  const skipAreaDeductionAmount =
    residentialFormulaQuoteOverride?.skipAreaDeductionAmount ??
    skipAreaDeductionRows.reduce((total, row) => total + row.amountCents, 0);
  const skipAreaDurationDeductionMinutes =
    residentialFormulaQuoteOverride?.skipAreaDurationDeductionMinutes ??
    Math.round(skipAreaDeductionRows.reduce((total, row) => total + row.oneMaidMinutes / teamSize, 0));
  const adjustedBaseAmount = residentialFormulaQuoteOverride
    ? resolvedBaseAmount
    : officeCommercialInstantQuoteOverride
      ? resolvedBaseAmount
    : Math.max(0, resolvedBaseAmount - skipAreaDeductionAmount);
  const adjustedDurationMinutes = residentialFormulaQuoteOverride
    ? resolvedDurationMinutes
    : officeCommercialInstantQuoteOverride
      ? resolvedDurationMinutes
    : Math.max(0, resolvedDurationMinutes - skipAreaDurationDeductionMinutes);
  const promotionRecord = findPromotion(catalog, input.promoCode);
  const promotion = promotionRecord && isPromotionValidForServiceDate(promotionRecord, input.serviceDate)
    ? {
        type: String(promotionRecord.discountType ?? promotionRecord.discount_type) as "fixed" | "percentage",
        value: Number(promotionRecord.discountValue ?? promotionRecord.discount_value)
      }
    : null;
  const frequencyRule = resolveFrequencyRule(catalog, input.frequencyRuleId, service);
  const arrivalFlexibilityState = resolveArrivalFlexibilityOptionState({
    settings: input.arrivalFlexibility?.settings,
    localStartTime: input.arrivalFlexibility?.localStartTime,
    selectedValue: adminDetails.arrival_flexibility
  });
  const customerSuppliesEquipmentSettings = input.customerSuppliesEquipment;
  const customerSuppliesEquipmentSelected = Boolean(
    customerSuppliesEquipmentSettings?.enabled && normalizeCustomerSuppliesProvidedBy(adminDetails)
  );
  const customerSuppliesEquipmentRequiredItems = normalizeStringList(customerSuppliesEquipmentSettings?.requiredItems);

  const optionVisitDurationTotalMinutes =
    residentialFormulaQuoteOverride || officeCommercialInstantQuoteOverride
      ? 0
      : convertLaborMinutesToVisitMinutes(resolvedOptionOneMaidMinutes, teamSize);
  const optionDurationMinutesForBaseQuote =
    optionVisitDurationTotalMinutes > 0
      ? [Math.max(0, roundUpToNearest15(adjustedDurationMinutes + optionVisitDurationTotalMinutes) - adjustedDurationMinutes)]
      : [];

  const baseQuote = calculateBaseQuote({
    baseAmount: adjustedBaseAmount,
    optionAmounts: resolvedOptions.map((option) => Number(option.resolvedPriceDeltaAmount ?? 0)),
    durationMinutes: adjustedDurationMinutes,
    optionDurationMinutes: optionDurationMinutesForBaseQuote,
    taxRate: input.taxRate,
    depositRate: input.depositRate
  });

  const frequencyDiscountBase = buildingCommonAreasInstantQuoteOverride || residentialFormulaQuoteOverride || officeCommercialInstantQuoteOverride
    ? adjustedBaseAmount
    : adjustedBaseAmount +
      resolvedOptions.reduce((total, option) => {
        if (Boolean(option.excludeFromFrequencyDiscount)) {
          return total;
        }
        return total + Number(option.resolvedPriceDeltaAmount ?? 0);
      }, 0);

  const frequencyDiscount = manualReviewOnly
    ? 0
    : frequencyRule
    ? buildingCommonAreasInstantQuoteOverride
      ? calculateBuildingCommonAreasFrequencyDiscount({
          baseAmountCents: adjustedBaseAmount,
          minimumChargeCents: buildingCommonAreasInstantQuoteOverride.minimumChargeCents,
          discountType: String(frequencyRule.discountType ?? frequencyRule.discount_type ?? "none"),
          discountValue: frequencyRule.discountValue ?? frequencyRule.discount_value
        })
      : calculateDiscountAmount(
          frequencyDiscountBase,
          String(frequencyRule.discountType ?? frequencyRule.discount_type ?? "none"),
          frequencyRule.discountValue ?? frequencyRule.discount_value
        )
    : 0;

  const promoExcludedAmount = resolvedOptions.reduce((total, option) => {
    return Boolean(option.excludeFromPromoDiscount) ? total + Number(option.resolvedPriceDeltaAmount ?? 0) : total;
  }, 0);
  const subtotalAfterFrequency = manualReviewOnly ? 0 : Math.max(0, baseQuote.subtotal - frequencyDiscount);
  const arrivalFlexibilityDiscount = !manualReviewOnly && arrivalFlexibilityState.selected
    ? applyArrivalFlexibilityDiscount({
        amountCents: subtotalAfterFrequency,
        discountType: arrivalFlexibilityState.settings.discountType,
        discountValue: arrivalFlexibilityState.settings.discountValue
      })
    : 0;
  const subtotalAfterArrivalFlexibility = Math.max(0, subtotalAfterFrequency - arrivalFlexibilityDiscount);
  const customerSuppliesEquipmentDiscount =
    !manualReviewOnly && !remodelingHourlyQuote && customerSuppliesEquipmentSelected
      ? Math.min(
          subtotalAfterArrivalFlexibility,
          normalizeCustomerSuppliesEquipmentDiscountCents(customerSuppliesEquipmentSettings)
        )
      : 0;
  const subtotalAfterCustomerSuppliesEquipment = Math.max(
    0,
    subtotalAfterArrivalFlexibility - customerSuppliesEquipmentDiscount
  );
  const promoDiscountBase = manualReviewOnly
    ? 0
    : Math.max(0, subtotalAfterCustomerSuppliesEquipment - promoExcludedAmount);

  let promoStatus: string | null = null;
  let promoMessage: string | null = null;
  let appliedPromotionCode: string | null = null;
  if (String(input.promoCode ?? "").trim()) {
    if (!promotionRecord) {
      promoStatus = "invalid";
      promoMessage = "This promo code isn't valid.";
    } else if (!normalizeActive(promotionRecord.active, false)) {
      promoStatus = "inactive";
      promoMessage = "This promo code isn't active.";
    } else {
      const currentPromotionStatus = getPromotionCurrentStatus(promotionRecord, input.now);
      if (currentPromotionStatus === "not_started") {
        promoStatus = "inactive";
        promoMessage = "This promo code isn't active yet.";
      } else if (currentPromotionStatus === "expired") {
        promoStatus = "expired";
        promoMessage = "This promo code has expired.";
      } else if (
        Number(promotionRecord.usageLimit ?? promotionRecord.usage_limit ?? 0) > 0 &&
        Number(promotionRecord.usageCount ?? promotionRecord.usage_count ?? 0) >=
          Number(promotionRecord.usageLimit ?? promotionRecord.usage_limit ?? 0)
      ) {
        promoStatus = "used";
        promoMessage = "This promo code has already been used.";
      } else if (!isPromotionValidForServiceDate(promotionRecord, input.serviceDate)) {
        promoStatus = "date";
        promoMessage = "This promo code doesn't apply to this date.";
      } else {
        const applicableServiceIds = normalizeStringList(
          promotionRecord.applicableServiceIds ??
            promotionRecord.applicableServiceIdsJson ??
            promotionRecord.applicable_service_ids_json
        );
        const minimumSpendAmount = Number(promotionRecord.minimumSpendAmount ?? promotionRecord.minimum_spend_amount ?? 0);
        if (applicableServiceIds.length && !applicableServiceIds.includes(input.serviceId)) {
          promoStatus = "service";
          promoMessage = "This promo code doesn't apply to this booking.";
        } else if (
          Number.isFinite(minimumSpendAmount) &&
          minimumSpendAmount > 0 &&
          subtotalAfterCustomerSuppliesEquipment < minimumSpendAmount
        ) {
          promoStatus = "minimum";
          promoMessage = `This promo code requires a minimum subtotal of ${formatCurrency(minimumSpendAmount)}.`;
        } else if (promoDiscountBase <= 0) {
          promoStatus = "service";
          promoMessage = "This promo code doesn't apply to this booking.";
        } else {
          promoStatus = "applied";
          promoMessage = "Promo code applied.";
          appliedPromotionCode = String(promotionRecord.code ?? "").trim().toUpperCase() || null;
        }
      }
    }
  }

  const promoDiscount =
    promotion == null || promoStatus !== "applied"
      ? 0
      : promotion.type === "fixed"
        ? Math.min(promoDiscountBase, promotion.value)
        : Math.min(promoDiscountBase, Math.round(promoDiscountBase * (promotion.value / 100)));
  const discount = frequencyDiscount + arrivalFlexibilityDiscount + customerSuppliesEquipmentDiscount + promoDiscount;
  const taxableAmount = Math.max(0, baseQuote.subtotal - discount);
  const tax = Math.round(taxableAmount * (input.taxRate ?? 0));
  const serviceTotal = taxableAmount + tax;
  const tip = resolveTipAmount(adminDetails, serviceTotal);
  const total = serviceTotal + tip;
  const deposit = Math.round(serviceTotal * (input.depositRate ?? 0));
  const amountDueNow = deposit > 0 ? deposit + tip : 0;
  const pricingDisplayMode = scenarioManualReviewOnly
    ? "manual_review" as const
    : remodelingHourlyQuote
      ? "remodeling_hourly" as const
      : remodelingReviewOnly
        ? "remodeling_review" as const
        : "exact" as const;
  const basePriceAmountForDisplay = manualReviewOnly
    ? 0
    : remodelingHourlyQuote
      ? remodelingHourlyQuote.startingAtAmount
      : adjustedBaseAmount;
  const configuredHourlyRateCentsPerMaidHourForDisplay =
    residentialFormulaQuoteOverride?.hourlyRateCentsPerMaidHour ??
    officeCommercialInstantQuoteOverride?.hourlyRateCentsPerMaidHour ??
    (remodelingHourlyQuote ? Math.round(remodelingHourlyQuote.hourlyRatePerMaidHour * 100) : skipAreaHourlyRateCentsPerMaidHour);
  const rawBaseDurationMinutesForDisplay = manualReviewOnly
    ? 0
    : remodelingHourlyQuote
      ? Math.round((remodelingHourlyQuote.minimumLaborHours / Math.max(1, remodelingHourlyQuote.minimumMaidCount)) * 60)
      : residentialFormulaQuoteOverride
        ? adjustVisitMinutesForTeamSize(residentialFormulaQuoteOverride.baseTeamMinutes, teamSize)
        : officeCommercialInstantQuoteOverride
          ? adjustVisitMinutesForTeamSize(officeCommercialInstantQuoteOverride.baseTeamMinutes, teamSize)
          : adjustedDurationMinutes;
  const baseDurationMinutesForDisplay =
    !manualReviewOnly &&
    !remodelingHourlyQuote &&
    configuredHourlyRateCentsPerMaidHourForDisplay > 0 &&
    basePriceAmountForDisplay > 0
      ? (basePriceAmountForDisplay / configuredHourlyRateCentsPerMaidHourForDisplay) * (60 / Math.max(1, teamSize))
      : rawBaseDurationMinutesForDisplay;
  const baseLaborHoursForDisplay = (baseDurationMinutesForDisplay * Math.max(1, teamSize)) / 60;
  const hourlyRateCentsPerMaidHourForDisplay =
    configuredHourlyRateCentsPerMaidHourForDisplay ||
    (baseLaborHoursForDisplay > 0 && basePriceAmountForDisplay > 0
      ? Math.round(basePriceAmountForDisplay / baseLaborHoursForDisplay)
      : 0);

  const quote = {
    subtotal: manualReviewOnly ? 0 : remodelingHourlyQuote ? remodelingHourlyQuote.startingAtAmount : baseQuote.subtotal,
    basePriceAmount: basePriceAmountForDisplay,
    baseDurationMinutes: baseDurationMinutesForDisplay,
    hourlyRateCentsPerMaidHour: hourlyRateCentsPerMaidHourForDisplay,
    discount: remodelingHourlyQuote || manualReviewOnly ? 0 : discount,
    tax: remodelingHourlyQuote || manualReviewOnly ? 0 : tax,
    total: manualReviewOnly ? 0 : remodelingHourlyQuote ? remodelingHourlyQuote.startingAtAmount : total,
    tip: remodelingHourlyQuote || manualReviewOnly ? 0 : tip,
    deposit: remodelingHourlyQuote || manualReviewOnly ? 0 : deposit,
    amountDueNow: remodelingHourlyQuote || manualReviewOnly ? 0 : amountDueNow,
    pricingDisplayMode,
    durationMinutes: manualReviewOnly
      ? 0
      : remodelingHourlyQuote
        ? Math.round(remodelingHourlyQuote.minimumLaborHours * 60)
        : baseQuote.durationMinutes
  };
  const buildingCommonAreasManualReviewNote = buildingCommonAreasManualReviewOnly
    ? "Due to the size of this building, we'll review the common-area scope manually. You can continue booking now with no payment due. We'll email approximate pricing and cleaning duration within 24 hours."
    : "";
  const officeCommercialManualReviewNote = officeCommercialManualReviewOnly
    ? "This office / commercial request needs manual review before we can confirm the final quote. You can continue booking now with no payment due. We'll email approximate pricing and cleaning duration within 24 hours."
    : "";

  return {
    ...quote,
    serviceName: String(service.name),
    optionNames: resolvedOptions.map((option) => String(option.name)),
    optionDetails: resolvedOptions.map((option) => ({
      id: String(option.id ?? ""),
      name: String(option.name ?? ""),
      priceDeltaAmount: Number(option.resolvedPriceDeltaAmount ?? 0),
      durationDeltaMinutes: Math.ceil(
        convertLaborMinutesToVisitMinutes(Number(option.resolvedDurationDeltaMinutes ?? 0), teamSize)
      ),
      quantity: Number(option.resolvedQuantity ?? 1),
      quantityLabel: String(option.resolvedQuantityLabel ?? "")
    })),
    promotionCode: appliedPromotionCode,
    promoStatus,
    promoMessage,
    frequencyRuleName: frequencyRule ? String(frequencyRule.name) : null,
    frequencyDiscount: remodelingHourlyQuote || manualReviewOnly ? 0 : frequencyDiscount,
    arrivalFlexibilityEligible: arrivalFlexibilityState.eligible,
    arrivalFlexibilitySelected: arrivalFlexibilityState.selected,
    arrivalFlexibilityLabel: arrivalFlexibilityState.label,
    arrivalFlexibilityDiscount: remodelingHourlyQuote || manualReviewOnly ? 0 : arrivalFlexibilityDiscount,
    customerSuppliesEquipmentSelected,
    customerSuppliesEquipmentDiscount: remodelingHourlyQuote || manualReviewOnly ? 0 : customerSuppliesEquipmentDiscount,
    customerSuppliesEquipmentRequiredItems,
    promoDiscount: remodelingHourlyQuote || manualReviewOnly ? 0 : promoDiscount,
    scenarioCode: String(resolvedScenario?.code ?? ""),
    scenarioName: String(resolvedScenario?.name ?? ""),
    apartmentFlatFeePackageCode: apartmentFlatFeeQuoteOverride?.packageCode ?? null,
    apartmentFlatFeePackageName: apartmentFlatFeeQuoteOverride?.displayName ?? null,
    scenarioPricingMode: scenarioManualReviewOnly
      ? "manual_review"
      : remodelingHourlyQuote
        ? "remodeling_hourly"
        : remodelingReviewOnly
          ? "remodeling_review"
          : String(resolvedScenario?.pricingMode ?? ""),
    scenarioCustomerNote: buildingCommonAreasManualReviewOnly
      ? buildingCommonAreasManualReviewNote
      : officeCommercialManualReviewOnly
        ? officeCommercialManualReviewNote
      : String(resolvedScenario?.customerNote ?? ""),
    pricingDisplayMode: quote.pricingDisplayMode,
    remodelingOptionCode:
      remodelingHourlyQuote?.remodelingTypeCode ??
      normalizeRemodelingOptionCode(adminDetails.remodeling_type ?? "") ??
      null,
    remodelingOptionLabel: remodelingHourlyQuote?.optionLabel ??
      (remodelingReviewOnly
        ? getRemodelingOptionLabel(
            normalizeRemodelingOptionCode(adminDetails.remodeling_type ?? ""),
            getSelectedRemodelingPricingCodes(adminDetails),
            adminDetails
          )
        : null),
    remodelingHourlyRatePerMaidHour: remodelingHourlyQuote?.hourlyRatePerMaidHour ?? 0,
    remodelingMinimumMaidCount: remodelingHourlyQuote?.minimumMaidCount ?? 0,
    remodelingMinimumHours: remodelingHourlyQuote?.minimumHours ?? 0,
    remodelingMinimumLaborHours: remodelingHourlyQuote?.minimumLaborHours ?? 0,
    remodelingStartingAtAmount: remodelingHourlyQuote?.startingAtAmount ?? 0,
    skipAreaDeductionAmount,
    skipAreaDurationDeductionMinutes,
    skipAreaDeductionRows
  };
}
